// src/components/AdminView.tsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { getApp } from 'firebase/app';
import { getFunctions, httpsCallable } from 'firebase/functions';

import {
  cfAdminCreateInvite,
  cfAdminListInvites,
  cfAdminDeleteInvite,
  cfAdminSetInviteEnabled,
  cfAdminSetAccessConfig,
  cfAdminApproveWaitlistedUser,
  cfAdminSendInviteEmail,
  cfAdminDeleteUserReport,
  cfAdminDeleteReportedPost,
  cfAdminDeleteReportedMarketplaceItem,
  cfAdminUpdateUserReportStatus,
  cfAdminWarnUserForReport,
  cfAdminUpdateSupportTicket,
  cfAdminProcessAccountDeletionRequests,
  cfAdminBackfillUserHandles,
  cfAdminSendAnnouncement,
  cfAdminGrantFoundingMemberRewards,
  cfAdminBackfillFoundingMemberRewards,
  cfAdminListUsers,
  cfAdminGetUserAuthSummaries,
  cfAdminGetUserAdminSnapshot,
  cfAdminGrantConsumables,
  cfAdminGetBazaarAuditEvents,
  type BazaarAuditEvent,
  type AdminUserAuthSummary,
} from '../services/adminClient';

import type {
  UserProfile,
  CommunityPost,
  AppUser,
  MarketplaceItem,
  Tournament,
} from '../types';

import {
  TrashIcon,
  MoreHorizontalIcon,
  ShieldIcon,
  MarketplaceIcon,
  SendIcon,
  EditIcon,
  UserIcon,
  ChartBarIcon,
  TrophyIcon,
  FlagIcon,
  XIcon,
} from './icons';

import { useTranslation } from 'react-i18next';
import { formatCurrency } from '../../i18n';
import ConfirmationModal from './ConfirmationModal';
import FishIQCalibrationPanel from './FishIQCalibrationPanel';
import { cfAdminSetTournamentConfig, DEFAULT_TOURNAMENT_CONFIG, subscribeTournamentConfig } from '../services/tournamentConfig';
import {
  adminReviewTournament,
  adminTournamentLifecycle,
  adminSetTournamentRegistration,
} from '../services/tournaments';
import type { TournamentPlatformConfig } from '../types';
import { FISHIQ_SPECIES_SEED } from '../constants/fishIQSeed';
import { buildFishIQDataHealthReport } from '../data/fishIQDataHealth';

/* 🔹 Firebase */
import { db } from '../lib/firebase';
import { collection, deleteDoc, doc, getCountFromServer, getDoc, limit, onSnapshot, orderBy, query, Timestamp, where } from 'firebase/firestore';

type AdminTab = 'stats' | 'users' | 'market' | 'tournaments' | 'access' | 'reports' | 'support' | 'deletions' | 'dev';

type AdminQueueCounts = {
  tournaments: number;
  reports: number;
  support: number;
  deletions: number;
};

const adminMobileFitStyles = `
  .roddo-admin-mobile input:not([type="checkbox"]):not([type="radio"]):not([type="range"]),
  .roddo-admin-mobile textarea,
  .roddo-admin-mobile select,
  .roddo-admin-mobile-modal input:not([type="checkbox"]):not([type="radio"]):not([type="range"]),
  .roddo-admin-mobile-modal textarea,
  .roddo-admin-mobile-modal select {
    scroll-margin-top: 14px;
    scroll-margin-bottom: 28px;
  }
  .roddo-admin-mobile-modal {
    overscroll-behavior: contain;
  }
`;

export interface AdminViewProps {
  posts: CommunityPost[];
  marketplaceItems: Record<string, MarketplaceItem>;
  userProfile: UserProfile;
  users: Record<string, AppUser>;
  tournaments: Record<string, Tournament>;
  currency: string;

  onUserAndPostsDeleted: (userId: string) => Promise<void> | void;
  onUpdateUserRoles: (
    userId: string,
    roles: { isAdmin?: boolean; isSeller?: boolean },
  ) => Promise<void> | void;
  onSendAnnouncement: (message: string) => Promise<void> | void;

  onPostDeleted: (postId: string) => Promise<void> | void;
  onItemDeleted: (itemId: string) => Promise<void> | void;
  onPostEdited: (postId: string, updatedData: Partial<CommunityPost>) => Promise<void> | void;
  onItemUpdated: (item: MarketplaceItem) => Promise<void> | void;

  onTournamentDelete: (tournamentId: string) => Promise<void> | void;


  onCancel: () => void;

  activeTab?: AdminTab;
  onTabChange?: (tab: AdminTab) => void;
}

const AdminPanel: React.FC<{
  children: React.ReactNode;
  className?: string;
  title?: string;
  icon?: React.ReactNode;
}> = ({ children, className = '', title, icon }) => (
  <div
    className={[
      'rounded-2xl border border-[#D8C4AA] bg-[#FFFDF8] text-[#22313A] shadow-[0_12px_28px_rgba(64,35,21,0.10)]',
      'dark:border-[rgba(132,151,190,.14)] dark:bg-[#101827] dark:text-[#F2F6FC] dark:shadow-[0_6px_16px_rgba(0,0,0,0.16)]',
      className,
    ].join(' ')}
  >
    {title ? (
      <div className="flex items-center gap-2 border-b border-[#D8C4AA] px-4 py-3 dark:border-[rgba(132,151,190,.14)]">
        {icon ? <span className="shrink-0 text-[#7B5E4C] dark:text-[#AEB8C9]">{icon}</span> : null}
        <h3 className="text-base font-black text-[#22313A] dark:text-white">{title}</h3>
      </div>
    ) : null}
    <div className={title ? 'p-4' : undefined}>{children}</div>
  </div>
);


type AdminConfirmOptions = {
  title: string;
  message: string;
  confirmText?: string;
};

function useAdminConfirmation() {
  const [request, setRequest] = useState<AdminConfirmOptions | null>(null);
  const resolverRef = useRef<((confirmed: boolean) => void) | null>(null);

  const close = useCallback((confirmed: boolean) => {
    const resolve = resolverRef.current;
    resolverRef.current = null;
    setRequest(null);
    resolve?.(confirmed);
  }, []);

  const confirm = useCallback((options: AdminConfirmOptions) => {
    resolverRef.current?.(false);

    return new Promise<boolean>((resolve) => {
      resolverRef.current = resolve;
      setRequest(options);
    });
  }, []);

  const confirmModal = (
    <ConfirmationModal
      isOpen={!!request}
      onClose={() => close(false)}
      onConfirm={() => close(true)}
      title={request?.title || 'Confirm action?'}
      message={request?.message || ''}
      confirmText={request?.confirmText || 'Confirm'}
    />
  );

  return { confirm, confirmModal };
}

const StatCard: React.FC<{
  title: string;
  value: string | number;
  prefix?: string;
  tone?: 'default' | 'warning' | 'success';
}> = ({ title, value, prefix, tone = 'default' }) => {
  const toneClass =
    tone === 'warning'
      ? 'bg-amber-500'
      : tone === 'success'
      ? 'bg-emerald-500'
      : 'bg-sky-500';

  return (
    <AdminPanel className="h-full px-3 py-3 sm:px-4 sm:py-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[11px] font-black uppercase tracking-[0.18em] text-[#7B5E4C] dark:text-[#AEB8C9]">
            {title}
          </p>
          <p className="mt-1.5 text-2xl font-black leading-none sm:text-3xl text-[#22313A] dark:text-white">
            {prefix}
            {value}
          </p>
        </div>
        <span className={['mt-1 h-2.5 w-2.5 rounded-full shadow-sm', toneClass].join(' ')} />
      </div>
    </AdminPanel>
  );
};

// ----------------------------
// MAIN ADMIN VIEW (UI GATED ONLY)
// ----------------------------
const AdminView: React.FC<AdminViewProps> = props => {
  const [localTab, setLocalTab] = useState<AdminTab>('stats');
  const activeTab = props.activeTab ?? localTab;
  const setActiveTab = props.onTabChange ?? setLocalTab;

  const isAdmin = !!(props.userProfile as any)?.isAdmin;
  const [adminUsers, setAdminUsers] = useState<Record<string, AppUser>>({});
  const [adminUsersLoaded, setAdminUsersLoaded] = useState(false);
  const [adminUsersLoading, setAdminUsersLoading] = useState(false);
  const [adminUsersError, setAdminUsersError] = useState<string | null>(null);
  const adminUsersRequestInFlightRef = useRef(false);
  const adminUsersInitialAttemptRef = useRef(false);

  const loadAdminUsers = useCallback(async () => {
    if (!isAdmin || adminUsersRequestInFlightRef.current) return;
    adminUsersRequestInFlightRef.current = true;
    setAdminUsersLoading(true);
    setAdminUsersError(null);

    try {
      const next: Record<string, AppUser> = {};
      let afterUid: string | null = null;
      let safety = 0;

      for (;;) {
        const res: any = await cfAdminListUsers({ pageSize: 500, afterUid });
        const data = res?.data || {};
        const page = Array.isArray(data?.users) ? data.users : [];

        page.forEach((row: any) => {
          const uid = String(row?.uid || '').trim();
          if (!uid) return;
          next[uid] = {
            ...(row as any),
            uid,
            id: uid,
            name: String(row?.name || row?.displayName || row?.username || 'Angler'),
            displayName: String(row?.displayName || row?.name || row?.username || 'Angler'),
            avatar: String(row?.avatar || ''),
            avatarUrl: String(row?.avatar || ''),
            photoURL: String(row?.avatar || ''),
          } as AppUser;
        });

        safety += 1;
        const nextCursor = String(data?.nextAfterUid || '').trim();
        if (!data?.hasMore || !nextCursor || safety >= 20) break;
        afterUid = nextCursor;
      }

      setAdminUsers(next);
      setAdminUsersLoaded(true);
    } catch (error: any) {
      console.error('[Admin users] authoritative directory failed', error);
      setAdminUsersError(error?.message || 'Unable to load the authoritative member directory.');
    } finally {
      adminUsersRequestInFlightRef.current = false;
      setAdminUsersLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    if (!isAdmin || adminUsersLoaded || adminUsersInitialAttemptRef.current) return;
    adminUsersInitialAttemptRef.current = true;
    void loadAdminUsers();
  }, [adminUsersLoaded, isAdmin, loadAdminUsers]);

  const effectiveUsers = adminUsersLoaded ? adminUsers : props.users;
  const adminProps = useMemo<AdminViewProps>(() => ({
    ...props,
    users: effectiveUsers,
    onUserAndPostsDeleted: async (userId: string) => {
      await Promise.resolve(props.onUserAndPostsDeleted(userId));
      setAdminUsers((prev) => {
        const next = { ...prev };
        delete next[userId];
        return next;
      });
    },
    onUpdateUserRoles: async (userId, roles) => {
      await Promise.resolve(props.onUpdateUserRoles(userId, roles));
      setAdminUsers((prev) => ({
        ...prev,
        [userId]: prev[userId]
          ? ({ ...prev[userId], ...roles } as AppUser)
          : prev[userId],
      }));
    },
  }), [effectiveUsers, props]);
  const pendingTournamentCount = useMemo(
    () =>
      (Object.values(props.tournaments || {}) as Tournament[]).filter(
        (tour) =>
          tour.status === 'pending_approval' ||
          tour.approvalStatus === 'pending',
      ).length,
    [props.tournaments],
  );
  const [queueCounts, setQueueCounts] = useState<AdminQueueCounts>({
    tournaments: 0,
    reports: 0,
    support: 0,
    deletions: 0,
  });

  useEffect(() => {
    if (!isAdmin) return;

    const cleanStatus = (value: unknown, fallback: string) =>
      String(value || fallback).trim().toLowerCase();

    const unsubs = [
      onSnapshot(
        query(collection(db, 'reports'), limit(250)),
        (snap) => {
          const count = snap.docs.reduce((sum, row) => {
            const status = cleanStatus((row.data() as any)?.status, 'open');
            return sum + (['open', 'reviewing', 'pending'].includes(status) ? 1 : 0);
          }, 0);
          setQueueCounts((current) => ({ ...current, reports: count }));
        },
        (error) => console.warn('[Admin queue] reports count unavailable', error),
      ),
      onSnapshot(
        query(collection(db, 'supportTickets'), limit(250)),
        (snap) => {
          const count = snap.docs.reduce((sum, row) => {
            const status = cleanStatus((row.data() as any)?.status, 'open');
            return sum + (['open', 'reviewing'].includes(status) ? 1 : 0);
          }, 0);
          setQueueCounts((current) => ({ ...current, support: count }));
        },
        (error) => console.warn('[Admin queue] support count unavailable', error),
      ),
      onSnapshot(
        query(collection(db, 'accountDeletionRequests'), limit(250)),
        (snap) => {
          const terminal = new Set(['completed', 'deleted', 'processed', 'cancelled', 'canceled']);
          const count = snap.docs.reduce((sum, row) => {
            const data = row.data() as any;
            const status = cleanStatus(data?.status || data?.accountDeletionStatus, 'requested');
            return sum + (terminal.has(status) ? 0 : 1);
          }, 0);
          setQueueCounts((current) => ({ ...current, deletions: count }));
        },
        (error) => console.warn('[Admin queue] deletion count unavailable', error),
      ),
    ];

    return () => unsubs.forEach((unsubscribe) => unsubscribe());
  }, [isAdmin]);

  const actionableQueueCounts = useMemo<AdminQueueCounts>(
    () => ({ ...queueCounts, tournaments: pendingTournamentCount }),
    [pendingTournamentCount, queueCounts],
  );
  const totalActionableCount =
    actionableQueueCounts.tournaments +
    actionableQueueCounts.reports +
    actionableQueueCounts.support +
    actionableQueueCounts.deletions;

  const TabButton: React.FC<{ name: AdminTab; label: string; badge?: number }> = ({
    name,
    label,
    badge = 0,
  }) => {
    const active = activeTab === name;

    return (
      <button
        type="button"
        role="tab"
        aria-selected={active}
        data-admin-tab={name}
        onClick={() => setActiveTab(name)}
        className={[
          'group inline-flex items-center justify-center rounded-xl px-3 py-2 text-[12px] font-extrabold transition sm:px-3.5 sm:text-[13px]',
          'border',
          active
            ? 'border-[#1F5E8C] bg-[#286F9F] text-white shadow-[0_6px_14px_rgba(40,111,159,0.18)] dark:border-[#8EA2FF]/55 dark:bg-[#8A70F2] dark:text-white'
            : 'border-transparent bg-transparent text-[#41535F] hover:border-[#D8C4AA] hover:bg-[#FFF8ED] dark:text-[#DDE5F1] dark:hover:border-[rgba(132,151,190,.14)] dark:hover:bg-[#0F1828]',
        ].join(' ')}
        title={label}
      >
        <span>{label}</span>
        {badge > 0 ? (
          <span
            className="ml-0.5 inline-flex min-w-[18px] items-center justify-center rounded-full bg-rose-600 px-1.5 py-0.5 text-[9px] font-black leading-none text-white shadow-sm"
            aria-label={`${badge} pending`}
          >
            {badge > 99 ? '99+' : badge}
          </span>
        ) : null}
      </button>
    );
  };

  if (!isAdmin) {
    return (
      <div className="p-4">
        <div className="rounded-2xl border border-[#D8C4AA] bg-[#FFFDF8] p-4 shadow-sm dark:border-[rgba(132,151,190,.14)] dark:bg-[#101827]">
          <p className="text-sm text-[#5B6577] dark:text-[#F4F6FA] font-semibold">Redirecting…</p>
          <p className="text-xs text-[#7B5E4C] dark:text-[#AEB8C9] mt-1">
            Admin tools are hidden unless your account is marked admin.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="roddo-admin-mobile mx-auto w-full max-w-[1480px] space-y-3 px-1 pb-6 pt-2 sm:px-2">
      <style>{adminMobileFitStyles}</style>

      <AdminPanel className="overflow-hidden">
        <div className="bg-[#FFF8ED] px-4 py-4 dark:bg-[#101827] sm:px-5 sm:py-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <div className="text-[10px] font-black uppercase tracking-[0.20em] text-[#7B5E4C] dark:text-[#AEB8C9]">
                Operations Console
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-2">
                <h2 className="text-[24px] font-black leading-tight text-[#22313A] dark:text-white sm:text-[28px]">
                  Roddo Admin
                </h2>
                <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-[9px] font-black uppercase tracking-[0.12em] text-emerald-700 dark:border-emerald-400/25 dark:bg-[#143228] dark:text-emerald-200">
                  Admin verified
                </span>
              </div>
              <p className="mt-1.5 max-w-3xl text-xs font-semibold leading-5 text-[#5B6577] dark:text-[#AEB8C9] sm:text-sm">
                Access, users, Bazaar moderation, tournament review, reports, support, privacy requests, and internal tools.
              </p>
            </div>

            <button
              type="button"
              onClick={() => {
                if (actionableQueueCounts.tournaments > 0) setActiveTab('tournaments');
                else if (actionableQueueCounts.reports > 0) setActiveTab('reports');
                else if (actionableQueueCounts.support > 0) setActiveTab('support');
                else if (actionableQueueCounts.deletions > 0) setActiveTab('deletions');
              }}
              disabled={totalActionableCount === 0}
              className={[
                'inline-flex min-h-10 shrink-0 items-center justify-center gap-2 rounded-xl border px-3 py-2 text-xs font-black transition',
                totalActionableCount > 0
                  ? 'border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100 dark:border-rose-400/25 dark:bg-rose-500/10 dark:text-rose-100 dark:hover:bg-rose-500/15'
                  : 'cursor-default border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-400/25 dark:bg-emerald-500/10 dark:text-emerald-200',
              ].join(' ')}
              aria-label={totalActionableCount > 0 ? `${totalActionableCount} admin items need attention` : 'All admin queues clear'}
            >
              {totalActionableCount > 0 ? (
                <>
                  <span className="inline-flex min-w-[20px] items-center justify-center rounded-full bg-rose-600 px-1.5 py-1 text-[10px] leading-none text-white">
                    {totalActionableCount > 99 ? '99+' : totalActionableCount}
                  </span>
                  <span>Needs attention</span>
                </>
              ) : (
                <span>All queues clear</span>
              )}
            </button>
          </div>
        </div>
      </AdminPanel>

      <div className="sticky top-0 z-40 min-w-0 rounded-xl border border-[#D8C4AA] bg-[#FFFDF8]/96 p-1.5 shadow-sm backdrop-blur-md dark:border-[rgba(132,151,190,.14)] dark:bg-[#0E1626]/96">
        <div
          role="tablist"
          aria-label="Roddo admin sections"
          className="flex w-full flex-wrap items-center gap-1"
        >
          <TabButton name="stats" label="Stats" />
          <TabButton name="users" label="Users" />
          <TabButton name="access" label="Access" />
          <TabButton name="market" label="Bazaar" />
          <TabButton name="tournaments" label="Tournaments" badge={actionableQueueCounts.tournaments} />
          <TabButton name="reports" label="Reports" badge={actionableQueueCounts.reports} />
          <TabButton name="support" label="Support" badge={actionableQueueCounts.support} />
          <TabButton name="deletions" label="Deletions" badge={actionableQueueCounts.deletions} />
          <TabButton name="dev" label="Dev" />
        </div>
      </div>

      <div className="min-w-0">
        {adminUsersError ? (
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-bold text-amber-900 dark:border-amber-400/25 dark:bg-amber-500/10 dark:text-amber-100">
            <span>{adminUsersError}</span>
            <button
              type="button"
              disabled={adminUsersLoading}
              onClick={() => void loadAdminUsers()}
              className="rounded-lg border border-amber-300 bg-white/80 px-2.5 py-1.5 text-[11px] font-black text-amber-900 disabled:opacity-60 dark:border-amber-300/30 dark:bg-black/20 dark:text-amber-100"
            >
              {adminUsersLoading ? 'Retrying…' : 'Retry directory'}
            </button>
          </div>
        ) : null}

        {activeTab === 'stats' && <StatsSection {...adminProps} />}
        {activeTab === 'access' && <AccessSection {...adminProps} />}
        {activeTab === 'users' && <UsersManagement {...adminProps} />}
        {activeTab === 'market' && <MarketplaceManagement {...adminProps} />}
        {activeTab === 'tournaments' && <TournamentManagement {...adminProps} />}
        {activeTab === 'reports' && <ReportsManagement {...adminProps} />}
        {activeTab === 'support' && <SupportTicketsManagement />}
        {activeTab === 'deletions' && (
          <AccountDeletionRequestsManagement
            users={adminProps.users}
            onUserAndPostsDeleted={props.onUserAndPostsDeleted}
          />
        )}
        {activeTab === 'dev' && <DevToolsSection users={adminProps.users} currentUserId={props.userProfile.uid} />}
      </div>
    </div>
  );
};

// ----------------------------
// ACCESS (CAP / INVITES / WAITLIST / EMAIL INVITE)
// ----------------------------
function AccessSection(props: AdminViewProps) {
  const { users } = props;

  const [waitlistEnabled, setWaitlistEnabled] = useState<boolean>(true);
  const [blockNewSignups, setBlockNewSignups] = useState<boolean>(false);

  const [newCode, setNewCode] = useState('');
  const [usesRemaining, setUsesRemaining] = useState<string>('');
  const [notes, setNotes] = useState('');

  const [inviteEmailTo, setInviteEmailTo] = useState('');
  const [inviteEmailSubject, setInviteEmailSubject] = useState('Your Roddo Invite Code');

  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [lastInviteCode, setLastInviteCode] = useState('');
  const [inviteCodes, setInviteCodes] = useState<any[]>([]);
  const [inviteCodesBusy, setInviteCodesBusy] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [backfillBusy, setBackfillBusy] = useState(false);
  const [backfillMsg, setBackfillMsg] = useState<string | null>(null);
  const [foundingBusy, setFoundingBusy] = useState(false);
  const [foundingMsg, setFoundingMsg] = useState<string | null>(null);
  const [activityCounts, setActivityCounts] = useState<{
    onlineNow: number;
    active1h: number;
    active24h: number;
    loadedAt: number;
  } | null>(null);
  const [activityCountsError, setActivityCountsError] = useState<string | null>(null);
  const { confirm, confirmModal } = useAdminConfirmation();

  useEffect(() => {
    let alive = true;

    async function loadAccessConfig() {
      try {
        const snap = await getDoc(doc(db, 'meta', 'access'));
        if (!alive) return;

        if (snap.exists()) {
          const data = snap.data() as any;
          setWaitlistEnabled(data?.waitlistEnabled !== false);
          setBlockNewSignups(data?.blockNewSignups === true);
        }
      } catch (e) {
        console.error('Failed to load access config', e);
        if (alive) setMsg('⚠️ Could not load access config. Showing local defaults.');
      } finally {
        if (alive) setIsLoaded(true);
      }
    }

    loadAccessConfig();
    return () => {
      alive = false;
    };
  }, []);

  const loadInviteCodes = useCallback(async () => {
    setInviteCodesBusy(true);
    try {
      const res: any = await cfAdminListInvites({ limit: 250 });
      const invites = Array.isArray(res?.data?.invites) ? res.data.invites : [];
      setInviteCodes(invites);
    } catch (e: any) {
      console.error(e);
      setMsg(`❌ Failed to load invite codes: ${String(e?.message || e)}`);
    } finally {
      setInviteCodesBusy(false);
    }
  }, []);

  useEffect(() => {
    loadInviteCodes();
  }, [loadInviteCodes]);

  const waitlisted = useMemo(() => {
    const arr = Object.values(users || {}) as any[];
    return arr
      .filter(u => (u as any)?.accessStatus === 'waitlisted')
      .sort(
        (a, b) =>
          Number((a as any)?.waitlistNumber ?? 0) - Number((b as any)?.waitlistNumber ?? 0),
      );
  }, [users]);

  const memberCount = useMemo(() => {
    return (Object.values(users || {}) as any[]).filter((u: any) => {
      const status = String(u?.accessStatus || u?.status || '').trim().toLowerCase();
      const memberNumber = Number(u?.memberNumber || 0);
      return (
        status === 'active' ||
        status === 'member' ||
        status === 'approved' ||
        u?.member === true ||
        u?.active === true ||
        u?.approved === true ||
        (Number.isFinite(memberNumber) && memberNumber > 0)
      );
    }).length;
  }, [users]);

  const loadActivityCounts = useCallback(async () => {
    const now = Date.now();
    const activity = collection(db, 'userActivity');

    try {
      setActivityCountsError(null);
      const [onlineSnap, oneHourSnap, oneDaySnap] = await Promise.all([
        getCountFromServer(
          query(activity, where('lastActiveAt', '>=', Timestamp.fromMillis(now - 15 * 60 * 1000))),
        ),
        getCountFromServer(
          query(activity, where('lastActiveAt', '>=', Timestamp.fromMillis(now - 60 * 60 * 1000))),
        ),
        getCountFromServer(
          query(activity, where('lastActiveAt', '>=', Timestamp.fromMillis(now - 24 * 60 * 60 * 1000))),
        ),
      ]);

      setActivityCounts({
        onlineNow: onlineSnap.data().count,
        active1h: oneHourSnap.data().count,
        active24h: oneDaySnap.data().count,
        loadedAt: Date.now(),
      });
    } catch (error) {
      console.error('[Admin activity metrics] failed', error);
      setActivityCountsError('Unable to load privacy-safe activity metrics.');
    }
  }, []);

  useEffect(() => {
    void loadActivityCounts();
    const timer = window.setInterval(() => void loadActivityCounts(), 2 * 60 * 1000);
    return () => window.clearInterval(timer);
  }, [loadActivityCounts]);

  const currentMode = blockNewSignups
    ? 'Closed signup'
    : 'Open signup';

  const cardSurfaceClass =
    'rounded-2xl border-2 border-[#D8C4AA] bg-[#FFFDF8] px-3 py-3 text-[#22313A] shadow-sm dark:border-[rgba(132,151,190,.14)] dark:bg-[#0C1423] dark:text-[#F4F6FA]';

  const fieldClass =
    'roddo-mobile-form mt-1 w-full rounded-2xl border border-[#D8C4AA] bg-[#FFFDF8] px-3 py-2.5 text-sm font-semibold text-[#22313A] outline-none placeholder:text-[#8A735F] focus:border-[#F1642E] focus:ring-2 focus:ring-[#F1642E]/25 dark:border-[rgba(132,151,190,.14)] dark:bg-[#0C1423] dark:text-[#F4F6FA] dark:placeholder:text-[#7F8BA0] dark:focus:border-[#8A70F2] dark:focus:ring-[#8A70F2]/20';

  const applyButtonClass =
    'w-full rounded-xl border border-[#B94A25] bg-[#F1642E] py-3 font-extrabold text-white shadow-[0_12px_30px_rgba(241,100,46,0.22)] transition hover:bg-[#D95225] disabled:cursor-not-allowed disabled:opacity-60 dark:border-[#F2B84B] dark:bg-[#F2B84B] dark:text-[#2D2615] dark:hover:bg-[#F7D37D] dark:disabled:text-[#24111C]';

  const secondaryButtonClass =
    'rounded-2xl border border-[#D8C4AA] bg-[#FFFDF8] px-3 py-2.5 text-sm font-bold text-[#22313A] shadow-sm transition hover:border-[#B8966E] hover:bg-[#FFF8ED] disabled:cursor-not-allowed disabled:opacity-60 disabled:text-[#8A735F] dark:border-[rgba(180,195,220,.16)] dark:bg-[#0C1423] dark:text-[#F4F6FA] dark:hover:border-[#8A70F2]/45 dark:hover:bg-[#0F1828] dark:disabled:text-[#7F8BA0]';

  const doSetConfig = async () => {
    setBusy(true);
    setMsg(null);
    try {
      const res: any = await cfAdminSetAccessConfig({
        inviteRequired: false,
        requirePromoCode: false,
        waitlistEnabled,
        blockNewSignups,
      } as any);
      if (res?.data?.ok !== true) throw new Error(res?.data?.error || 'Access config update failed');
      setMsg(`✅ Access config updated — ${currentMode}`);
    } catch (e: any) {
      console.error(e);
      setMsg(`❌ Failed to update access config: ${String(e?.message || e)}`);
    } finally {
      setBusy(false);
    }
  };

  const doCreateInvite = async () => {
    const uses = usesRemaining.trim();
    const usesNum = uses ? Number(uses) : null;
    if (uses && !Number.isFinite(usesNum as any)) {
      setMsg('❌ Uses remaining must be a number (or blank).');
      return;
    }

    setBusy(true);
    setMsg(null);
    try {
      const payload: any = {
        code: newCode.trim() || undefined,
        usesRemaining: uses ? Number(uses) : null,
        notes: notes.trim() || undefined,
      };

      const res: any = await cfAdminCreateInvite(payload);
      const code = (res?.data?.code || '').toString().trim().toUpperCase();
      if (code) {
        setNewCode(code);
        setLastInviteCode(code);
      }
      setMsg(`✅ Invite code created${code ? `: ${code}` : ''}`);
      await loadInviteCodes();
    } catch (e: any) {
      console.error(e);
      setMsg(`❌ Failed to create invite: ${String(e?.message || e)}`);
    } finally {
      setBusy(false);
    }
  };

  const copyInviteLink = async (explicitCode?: string) => {
    const code = (explicitCode || lastInviteCode || newCode).trim().toUpperCase();
    if (!code) {
      setMsg('❌ Create (or paste) a invite code first.');
      return;
    }

    const base = ((import.meta as any).env?.VITE_APP_URL || `${window.location.origin}/app`).replace(/\/$/, '');
    const url = `${base}?invite=${encodeURIComponent(code)}`;

    try {
      await navigator.clipboard.writeText(url);
      setMsg(`✅ Copied invite link: ${url}`);
    } catch (e) {
      console.error(e);
      window.prompt('Copy this invite link:', url);
      setMsg('✅ Invite link ready. Manual copy prompt opened.');
    }
  };

  const doToggleInvite = async (enabled: boolean, explicitCode?: string) => {
    const code = (explicitCode || lastInviteCode || newCode).trim().toUpperCase();
    if (!code) {
      setMsg('❌ Create (or paste) a invite code first.');
      return;
    }

    setBusy(true);
    setMsg(null);
    try {
      await cfAdminSetInviteEnabled({ code, enabled } as any);
      setMsg(`✅ Invite code ${enabled ? 'enabled' : 'disabled'}: ${code}`);
      await loadInviteCodes();
    } catch (e: any) {
      console.error(e);
      setMsg(`❌ Failed to update invite: ${String(e?.message || e)}`);
    } finally {
      setBusy(false);
    }
  };

  const doDeleteInvite = async (codeRaw: string) => {
    const code = String(codeRaw || '').trim().toUpperCase();
    if (!code) {
      setMsg('❌ Missing invite code.');
      return;
    }

    const ok = await confirm({
      title: `Delete invite code ${code}?`,
      message: 'This will disable and hide it from the admin inventory. This cannot be undone from the app.',
      confirmText: 'Delete',
    });
    if (!ok) return;

    setBusy(true);
    setMsg(null);
    try {
      await cfAdminDeleteInvite({ code } as any);
      if ((lastInviteCode || '').toUpperCase() === code) setLastInviteCode('');
      if ((newCode || '').toUpperCase() === code) setNewCode('');
      setMsg(`✅ Invite code deleted: ${code}`);
      await loadInviteCodes();
    } catch (e: any) {
      console.error(e);
      setMsg(`❌ Failed to delete invite code: ${String(e?.message || e)}`);
    } finally {
      setBusy(false);
    }
  };

  const doApprove = async (uid: string) => {
    setBusy(true);
    setMsg(null);
    try {
      await cfAdminApproveWaitlistedUser({ uid } as any);
      setMsg(`✅ Approved waitlisted user: ${uid}`);
    } catch (e: any) {
      console.error(e);
      setMsg(`❌ Failed to approve: ${String(e?.message || e)}`);
    } finally {
      setBusy(false);
    }
  };

  const doSendInviteEmail = async () => {
    const to = inviteEmailTo.trim();
    const code = (lastInviteCode || newCode).trim().toUpperCase();

    if (!to || !to.includes('@')) {
      setMsg('❌ Enter a valid recipient email.');
      return;
    }
    if (!code) {
      setMsg('❌ Create (or paste) a invite code first.');
      return;
    }

    setBusy(true);
    setMsg(null);
    try {
      await cfAdminSendInviteEmail({
        to,
        code,
        subject: inviteEmailSubject.trim() || 'Your Roddo Invite Code',
      } as any);
      setMsg(`✅ Invite email sent to: ${to}`);
    } catch (e: any) {
      console.error(e);
      setMsg(`❌ Failed to send email: ${String(e?.message || e)}`);
    } finally {
      setBusy(false);
    }
  };

  const doBackfillFoundingRewards = async () => {
    setFoundingBusy(true);
    setFoundingMsg(null);
    try {
      const res: any = await cfAdminBackfillFoundingMemberRewards({ limit: 5000 });
      const data = res?.data || {};
      setFoundingMsg(
        `✅ Founding rewards complete. Scanned ${Number(data.scanned || 0)}, eligible ${Number(data.eligible || 0)}, granted ${Number(data.granted || 0)}, already granted ${Number(data.skipped || 0)}, failed ${Number(data.failed || 0)}.`,
      );
    } catch (e: any) {
      console.error(e);
      setFoundingMsg(`❌ Founding reward backfill failed: ${String(e?.message || e)}`);
    } finally {
      setFoundingBusy(false);
    }
  };

  const doBackfillUserHandles = async () => {
    setBackfillBusy(true);
    setBackfillMsg(null);
    try {
      const res: any = await cfAdminBackfillUserHandles({ limit: 1000 });
      const scanned = Number(res?.data?.scanned ?? 0);
      const updated = Number(res?.data?.updated ?? 0);
      setBackfillMsg(`✅ User handle backfill complete. Scanned ${scanned} users and updated ${updated}.`);
    } catch (e: any) {
      console.error(e);
      setBackfillMsg(`❌ Failed to run handle backfill: ${String(e?.message || e)}`);
    } finally {
      setBackfillBusy(false);
    }
  };

  return (
    <>
      <div className="space-y-4 px-1">
      <AdminPanel className="p-4 sm:p-5">
        <div className="space-y-4">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.18em] text-[#7B5E4C] dark:text-[#AEB8C9]">
              Membership Access
            </div>
            <h3 className="mt-1 text-lg font-bold">Signup + access controls</h3>
            <p className="mt-1 text-sm text-[#5B6577] dark:text-[#AEB8C9]">
              Open or pause new account creation without touching backend files. Promo codes remain optional.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-2 lg:grid-cols-3">
            <div className={cardSurfaceClass}>
              <div className="flex items-center justify-between gap-2">
                <div className="text-[11px] uppercase tracking-[0.16em] text-[#7B5E4C] dark:text-[#AEB8C9]">Online now</div>
                <span className="h-2 w-2 rounded-full bg-emerald-500" />
              </div>
              <div className="mt-1 text-2xl font-black tabular-nums">{activityCounts?.onlineNow ?? '—'}</div>
              <div className="mt-1 text-[10px] font-semibold text-[#7B5E4C] dark:text-[#8290A7]">active within 15m</div>
            </div>
            <div className={cardSurfaceClass}>
              <div className="text-[11px] uppercase tracking-[0.16em] text-[#7B5E4C] dark:text-[#AEB8C9]">Members</div>
              <div className="mt-1 text-2xl font-black tabular-nums">{memberCount}</div>
              <div className="mt-1 text-[10px] font-semibold text-[#7B5E4C] dark:text-[#8290A7]">admitted accounts</div>
            </div>
            <div className={cardSurfaceClass}>
              <div className="text-[11px] uppercase tracking-[0.16em] text-[#7B5E4C] dark:text-[#AEB8C9]">Waitlist</div>
              <div className="mt-1 text-2xl font-black tabular-nums">{waitlisted.length}</div>
              <div className="mt-1 text-[10px] font-semibold text-[#7B5E4C] dark:text-[#8290A7]">pending access</div>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-[#D8C4AA] bg-[#FFF8ED] px-3 py-2.5 dark:border-[rgba(132,151,190,.14)] dark:bg-[#0F1828]">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs font-bold text-[#314653] dark:text-[#E7ECF5]">
              <span>Active 1h <b className="tabular-nums">{activityCounts?.active1h ?? '—'}</b></span>
              <span>Active 24h <b className="tabular-nums">{activityCounts?.active24h ?? '—'}</b></span>
              <span className="font-semibold text-[#7B5E4C] dark:text-[#8290A7]">Privacy-safe aggregate · no GPS</span>
            </div>
            <button
              type="button"
              onClick={() => void loadActivityCounts()}
              className="rounded-lg border border-[#D8C4AA] bg-[#FFFDF8] px-2.5 py-1.5 text-[11px] font-black text-[#314653] hover:bg-white dark:border-[rgba(180,195,220,.16)] dark:bg-[#0C1423] dark:text-white"
            >
              Refresh
            </button>
          </div>

          {activityCountsError ? (
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-900 dark:border-amber-400/25 dark:bg-amber-500/10 dark:text-amber-100">
              {activityCountsError}
            </div>
          ) : null}

          <label className={cardSurfaceClass + ' flex items-center justify-between gap-3 cursor-pointer'}>
            <div>
              <div className="text-sm font-bold">Allow new signups</div>
              <div className="text-xs text-[#6A4C38] dark:text-[#AEB8C9]">Turn this off to fully pause new user access.</div>
            </div>
            <input
              type="checkbox"
              checked={!blockNewSignups}
              onChange={e => setBlockNewSignups(!e.target.checked)}
              className="h-5 w-5"
            />
          </label>

          <label className={cardSurfaceClass + ' flex items-center justify-between gap-3 cursor-pointer'}>
            <div>
              <div className="text-sm font-bold">Enable waitlist</div>
              <div className="text-xs text-[#6A4C38] dark:text-[#AEB8C9]">Keep this available for legacy waitlisted accounts and manual rollout handling.</div>
            </div>
            <input
              type="checkbox"
              checked={waitlistEnabled}
              onChange={e => setWaitlistEnabled(e.target.checked)}
              className="h-5 w-5"
            />
          </label>

          <div className="rounded-2xl border-2 border-amber-400/30 bg-amber-50 dark:bg-[#332B1F] px-3 py-3">
            <div className="text-sm font-bold">Current mode</div>
            <div className="mt-1 text-sm">{currentMode}</div>
            {!isLoaded && <div className="mt-1 text-xs text-[#6A4C38] dark:text-[#AEB8C9]">Loading live config…</div>}
          </div>

          <button className={applyButtonClass} onClick={doSetConfig} disabled={busy}>
            Apply Access Config
          </button>

          {msg && (
            <div className="rounded-2xl border border-[#D8C4AA] dark:border-[rgba(132,151,190,.14)] bg-[#FFFDF8] dark:bg-[#0C1423] px-3 py-3 text-sm font-semibold whitespace-pre-wrap">
              {msg}
            </div>
          )}
        </div>
      </AdminPanel>

      <AdminPanel className="p-4 sm:p-5">
        <div className="space-y-4">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.18em] text-[#7B5E4C] dark:text-[#AEB8C9]">Invite Codes</div>
            <h3 className="mt-1 text-lg font-bold">Create and manage invite codes</h3>
            <p className="mt-1 text-sm text-[#5B6577] dark:text-[#AEB8C9]">
              Generate a code for founders, beta waves, staff, or controlled public rollout.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block">
              <div className="text-xs font-bold text-[#5B6577] dark:text-[#AEB8C9] mb-1">Invite code</div>
              <input
                className={`${fieldClass} uppercase`}
                value={newCode}
                onChange={e => setNewCode(e.target.value.toUpperCase())}
                placeholder="AUTO-GEN or CUSTOMCODE"
              />
            </label>

            <label className="block">
              <div className="text-xs font-bold text-[#5B6577] dark:text-[#AEB8C9] mb-1">Uses remaining</div>
              <input
                className={fieldClass}
                value={usesRemaining}
                onChange={e => setUsesRemaining(e.target.value)}
                placeholder="Blank = unlimited"
                inputMode="numeric"
              />
            </label>

            <label className="block sm:col-span-2">
              <div className="text-xs font-bold text-[#5B6577] dark:text-[#AEB8C9] mb-1">Notes</div>
              <input
                className={fieldClass}
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="Founders / beta wave / staff / creators"
              />
            </label>
          </div>

          <div className="flex flex-wrap gap-2">
            <button className={applyButtonClass + ' sm:w-auto sm:px-4'} onClick={doCreateInvite} disabled={busy}>
              Create Invite Code
            </button>
            <button className={secondaryButtonClass} onClick={() => doToggleInvite(true)} disabled={busy}>
              Enable Code
            </button>
            <button className={secondaryButtonClass} onClick={() => doToggleInvite(false)} disabled={busy}>
              Disable Code
            </button>
            <button className={secondaryButtonClass} onClick={() => copyInviteLink()} disabled={busy}>
              Copy Invite Link
            </button>
          </div>

          {(lastInviteCode || newCode.trim()) && (
            <div className={cardSurfaceClass}>
              <div className="text-xs font-bold text-[#6A4C38] dark:text-[#AEB8C9] mb-1">Current code</div>
              <code className="text-base font-black">{(lastInviteCode || newCode).trim().toUpperCase()}</code>
            </div>
          )}


          <div className="rounded-2xl border-2 border-[#D8C4AA] dark:border-[rgba(132,151,190,.14)] p-3 space-y-3 bg-[#FFF8ED] dark:bg-[#0C1423]">
            <div className="sticky top-0 z-20 flex shrink-0 items-start justify-between gap-3 border-b border-[#D8C4AA] bg-[#FFFDF8]/95 p-5 backdrop-blur-md dark:border-[rgba(132,151,190,.14)] dark:bg-[#0C1423]/95">
              <div>
                <div className="text-xs font-bold uppercase tracking-[0.18em] text-[#7B5E4C] dark:text-[#AEB8C9]">Invite Code Inventory</div>
                <p className="mt-1 text-xs text-[#5B6577] dark:text-[#AEB8C9]">
                  Track beta waves, remaining seats, and used invites from one admin panel.
                </p>
              </div>
              <button
                type="button"
                className={secondaryButtonClass}
                onClick={loadInviteCodes}
                disabled={busy || inviteCodesBusy}
              >
                {inviteCodesBusy ? 'Refreshing…' : 'Refresh Codes'}
              </button>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <div className={cardSurfaceClass}>
                <div className="text-[10px] uppercase tracking-[0.16em] text-[#7B5E4C] dark:text-[#AEB8C9]">Codes</div>
                <div className="mt-1 text-xl font-black">{inviteCodes.length}</div>
              </div>
              <div className={cardSurfaceClass}>
                <div className="text-[10px] uppercase tracking-[0.16em] text-[#7B5E4C] dark:text-[#AEB8C9]">Enabled</div>
                <div className="mt-1 text-xl font-black">{inviteCodes.filter(c => c.enabled).length}</div>
              </div>
              <div className={cardSurfaceClass}>
                <div className="text-[10px] uppercase tracking-[0.16em] text-[#7B5E4C] dark:text-[#AEB8C9]">Used</div>
                <div className="mt-1 text-xl font-black">
                  {inviteCodes.reduce((sum, c) => sum + Number(c.usesUsed || 0), 0)}
                </div>
              </div>
              <div className={cardSurfaceClass}>
                <div className="text-[10px] uppercase tracking-[0.16em] text-[#7B5E4C] dark:text-[#AEB8C9]">Available</div>
                <div className="mt-1 text-xl font-black">
                  {inviteCodes.reduce((sum, c) => sum + (c.usesRemaining === null ? 0 : Number(c.usesRemaining || 0)), 0)}
                  {inviteCodes.some(c => c.usesRemaining === null) ? ' + 1∞' : ''}
                </div>
              </div>
            </div>

            {inviteCodes.length === 0 ? (
              <p className="text-sm text-[#5B6577] dark:text-[#AEB8C9]">No invite codes found yet.</p>
            ) : (
              <div className="space-y-2">
                {inviteCodes.map((invite: any) => {
                  const code = String(invite.code || '').toUpperCase();
                  const remainingLabel =
                    invite.usesRemaining === null || invite.usesRemaining === undefined
                      ? 'unlimited'
                      : `${Number(invite.usesRemaining || 0)} left`;
                  const totalLabel =
                    invite.usesTotal === null || invite.usesTotal === undefined
                      ? 'unlimited'
                      : `${Number(invite.usesTotal || 0)} total`;

                  return (
                    <div key={code} className={cardSurfaceClass + ' space-y-2'}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <code className="rounded-xl bg-[#FFFDF8] dark:bg-[#0C1423] px-2 py-1 text-xs font-black">
                              {code}
                            </code>
                            <span
                              className={
                                invite.enabled
                                  ? 'rounded-full bg-teal-500/25 px-2 py-1 text-[10px] font-black text-teal-800 dark:text-teal-100'
                                  : 'rounded-full bg-zinc-500/25 px-2 py-1 text-[10px] font-black text-zinc-700 dark:text-zinc-200'
                              }
                            >
                              {invite.enabled ? 'ENABLED' : 'DISABLED'}
                            </span>
                          </div>
                          {invite.notes && <div className="mt-2 text-xs font-semibold text-[#5B6577] dark:text-[#AEB8C9]">{invite.notes}</div>}
                        </div>

                        <div className="text-right text-xs font-bold text-[#5B6577] dark:text-[#AEB8C9]">
                          <div>
                            {Number(invite.usesUsed || 0)} used / {remainingLabel} / {totalLabel}
                          </div>
                          <div>Last used: {invite.lastUsedAt ? new Date(invite.lastUsedAt).toLocaleDateString() : '—'}</div>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px] font-bold text-[#5B6577] dark:text-[#AEB8C9]">
                        <div>Created: {invite.createdAt ? new Date(invite.createdAt).toLocaleDateString() : '—'}</div>
                        <div>Expires: {invite.expiresAt ? new Date(invite.expiresAt).toLocaleDateString() : '—'}</div>
                        <div className="truncate">Used by: {invite.lastUsedBy || '—'}</div>
                        <div>Updated: {invite.updatedAt ? new Date(invite.updatedAt).toLocaleDateString() : '—'}</div>
                      </div>

                      <div className="flex flex-wrap gap-2">
                        <button type="button" className={secondaryButtonClass} onClick={() => copyInviteLink(code)} disabled={busy}>
                          Copy Link
                        </button>
                        <button
                          type="button"
                          className={secondaryButtonClass}
                          onClick={() => {
                            setLastInviteCode(code);
                            setNewCode(code);
                            doToggleInvite(!invite.enabled, code);
                          }}
                          disabled={busy}
                        >
                          {invite.enabled ? 'Disable' : 'Enable'}
                        </button>
                        <button
                          type="button"
                          className="rounded-2xl border border-red-300/70 bg-red-50 px-3 py-2.5 text-sm font-bold text-red-700 transition hover:bg-red-100 disabled:opacity-60 disabled:text-[#8A735F] dark:border-red-300/40 dark:bg-[#3A1720] dark:text-red-100 dark:hover:bg-[#4A1D28] dark:disabled:text-red-200"
                          onClick={() => doDeleteInvite(code)}
                          disabled={busy}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="rounded-2xl border-2 border-[#D8C4AA] dark:border-[rgba(132,151,190,.14)] p-3 space-y-2 bg-[#FFF8ED] dark:bg-[#0C1423]">
            <div className="text-xs font-bold uppercase tracking-[0.18em] text-[#7B5E4C] dark:text-[#AEB8C9]">Send invite email</div>
            <input
              className={fieldClass}
              value={inviteEmailTo}
              onChange={e => setInviteEmailTo(e.target.value)}
              placeholder="Recipient email"
            />
            <input
              className={fieldClass}
              value={inviteEmailSubject}
              onChange={e => setInviteEmailSubject(e.target.value)}
              placeholder="Email subject"
            />
            <button className={applyButtonClass} onClick={doSendInviteEmail} disabled={busy}>
              Send Invite Email
            </button>
            <p className="text-[11px] text-[#5B6577] dark:text-[#AEB8C9]">
              Requires SENDGRID_API_KEY + SENDGRID_FROM_EMAIL secrets.
            </p>
          </div>
        </div>
      </AdminPanel>

      <AdminPanel className="p-4 sm:p-5">
        <div className="space-y-3">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.18em] text-[#7B5E4C] dark:text-[#AEB8C9]">Waitlist</div>
            <h3 className="mt-1 text-lg font-bold">Approve waitlisted users</h3>
          </div>

          {waitlisted.length === 0 ? (
            <p className="text-sm text-[#5B6577] dark:text-[#AEB8C9]">No waitlisted users detected in the current users snapshot.</p>
          ) : (
            <div className="space-y-2">
              {waitlisted.slice(0, 50).map((u: any) => (
                <div key={u.uid} className={cardSurfaceClass + ' flex items-center justify-between gap-3'}>
                  <div className="min-w-0">
                    <p className="font-semibold truncate">{u.name || u.displayName || u.uid}</p>
                    <p className="text-xs text-[#5B6577] dark:text-[#AEB8C9]">waitlist #{Number(u.waitlistNumber ?? 0) || '—'}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => doApprove(u.uid)}
                    className={applyButtonClass + ' w-auto px-4 py-2'}
                    disabled={busy}
                  >
                    Approve
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </AdminPanel>

      <AdminPanel className="p-4 sm:p-5">
        <div className="space-y-4">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.18em] text-[#7B5E4C] dark:text-[#AEB8C9]">Founding Member Rewards</div>
            <h3 className="mt-1 text-lg font-bold">Grant the Founding 5K package</h3>
            <p className="mt-1 text-sm text-[#5B6577] dark:text-[#AEB8C9]">
              Idempotently grants eligible members 500 Shells, three exclusive cosmetics, 10 featured listings, 12 listing bumps, and 5 tournament entries.
            </p>
          </div>
          <button type="button" className={applyButtonClass} onClick={doBackfillFoundingRewards} disabled={busy || foundingBusy}>
            {foundingBusy ? 'Granting Founding Rewards…' : 'Grant Pending Founding Rewards'}
          </button>
          {foundingMsg && (
            <div className="rounded-2xl border border-[#D8C4AA] dark:border-[rgba(132,151,190,.14)] bg-[#FFFDF8] dark:bg-[#0C1423] px-3 py-3 text-sm font-semibold whitespace-pre-wrap">
              {foundingMsg}
            </div>
          )}
        </div>
      </AdminPanel>

      <AdminPanel className="p-4 sm:p-5">
        <div className="space-y-4">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.18em] text-[#7B5E4C] dark:text-[#AEB8C9]">User Handles</div>
            <h3 className="mt-1 text-lg font-bold">Backfill mention handles</h3>
            <p className="mt-1 text-sm text-[#5B6577] dark:text-[#AEB8C9]">
              Normalize existing user docs so mentions resolve reliably against username and handle fields.
            </p>
          </div>

          <div className={cardSurfaceClass}>
            <div className="text-sm font-bold">One-time repair</div>
            <p className="mt-1 text-xs text-[#6A4C38] dark:text-[#AEB8C9]">
              This scans current user docs and writes missing normalized fields like usernameLower and handleLower.
            </p>
          </div>

          <button
            type="button"
            className={applyButtonClass}
            onClick={doBackfillUserHandles}
            disabled={busy || backfillBusy}
          >
            {backfillBusy ? 'Running Handle Backfill…' : 'Run User Handle Backfill'}
          </button>

          {backfillMsg && (
            <div className="rounded-2xl border border-[#D8C4AA] dark:border-[rgba(132,151,190,.14)] bg-[#FFFDF8] dark:bg-[#0C1423] px-3 py-3 text-sm font-semibold whitespace-pre-wrap">
              {backfillMsg}
            </div>
          )}
        </div>
      </AdminPanel>
      </div>
      {confirmModal}
    </>
  );
}


const csvEscape = (value: unknown): string => {
  const text = value === null || value === undefined ? '' : String(value);
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

const rowsToCsv = (headers: string[], rows: Array<Record<string, unknown>>): string => {
  const lines = [headers.map(csvEscape).join(',')];
  rows.forEach(row => {
    lines.push(headers.map(header => csvEscape(row[header])).join(','));
  });
  return lines.join('\n');
};

const safeMetricRows = (section: string, rows: any[] | undefined) =>
  (rows || []).map(row => ({
    section,
    label: row?.label ?? 'unknown',
    count: row?.count ?? 0,
  }));

const buildFishIQPilotMetricsCsv = (metrics: any): string => {
  const totals = metrics?.totals || {};
  const generatedAtMs = metrics?.generatedAtMs || metrics?.generatedAt || Date.now();
  const sinceMs = metrics?.sinceMs || '';
  const privacyNote = metrics?.privacyNote || 'Aggregate-only export. No UID, identity, exact GPS, photos, or raw notes are included.';

  const rows: Array<Record<string, unknown>> = [
    { section: 'metadata', label: 'generatedAtIso', count: new Date(generatedAtMs).toISOString() },
    { section: 'metadata', label: 'sinceIso', count: sinceMs ? new Date(Number(sinceMs)).toISOString() : '' },
    { section: 'metadata', label: 'privacyNote', count: privacyNote },
    { section: 'totals', label: 'observations', count: totals.observations ?? 0 },
    { section: 'totals', label: 'sharedAggregateEligible', count: totals.sharedAggregateEligible ?? 0 },
    { section: 'totals', label: 'privateOnly', count: totals.privateOnly ?? 0 },
    { section: 'totals', label: 'rollupEligible', count: totals.rollupEligible ?? 0 },
    { section: 'totals', label: 'generalizedPrivacyProtected', count: totals.generalizedPrivacyProtected ?? 0 },
    { section: 'totals', label: 'pilotParticipants', count: totals.pilotParticipants ?? 0 },
    ...safeMetricRows('species', metrics?.topSpecies),
    ...safeMetricRows('generalizedWaters', metrics?.topWaters),
    ...safeMetricRows('regionBuckets', metrics?.topRegions),
    ...safeMetricRows('monthBuckets', metrics?.monthBuckets),
    ...safeMetricRows('partnerCohorts', metrics?.partnerCodeBuckets),
    ...safeMetricRows('waterClarity', metrics?.waterClarityBuckets),
    ...safeMetricRows('catchOutcomes', metrics?.catchDispositionBuckets),
  ];

  return rowsToCsv(['section', 'label', 'count'], rows);
};

const downloadCsv = (filename: string, csv: string) => {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
};

const makeExportStamp = () => new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');

// ----------------------------
// STATS + ANNOUNCEMENTS
// ----------------------------
const StatsSection: React.FC<AdminViewProps> = ({
  users,
  tournaments,
}) => {
  const activeTournaments = (Object.values(tournaments) as Tournament[]).filter(
    t => (t as any).status === 'active',
  );
  const [fishIQPilotMetrics, setFishIQPilotMetrics] = useState<any | null>(null);
  const [fishIQPilotMetricsError, setFishIQPilotMetricsError] = useState<string | null>(null);
  const [fishIQPilotMetricsLoading, setFishIQPilotMetricsLoading] = useState(false);

  const loadFishIQPilotMetrics = useCallback(async () => {
    setFishIQPilotMetricsLoading(true);
    setFishIQPilotMetricsError(null);
    try {
      const fn = httpsCallable(getFunctions(getApp(), 'us-central1'), 'adminGetFishIQPilotMetrics');
      const res = await fn({ sinceMs: Date.now() - 90 * 24 * 60 * 60 * 1000, limit: 1000 });
      setFishIQPilotMetrics((res as any)?.data ?? null);
    } catch (err: any) {
      console.error('[Admin FishIQ Pilot Metrics] failed', err);
      setFishIQPilotMetricsError(err?.message || 'Unable to load FishIQ pilot metrics. Redeploy functions and confirm admin role.');
    } finally {
      setFishIQPilotMetricsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadFishIQPilotMetrics();
  }, [loadFishIQPilotMetrics]);

  const pilotTotals = fishIQPilotMetrics?.totals || {};
  const fishIQDataHealth = useMemo(() => buildFishIQDataHealthReport(FISHIQ_SPECIES_SEED), []);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <StatCard title="Total Users" value={Object.keys(users).length} />
        <StatCard title="Active Tournaments" value={activeTournaments.length} />
      </div>

      <AdminPanel title="FishIQ Pilot Metrics" icon={<ChartBarIcon className="h-5 w-5" />}>
        <div className="space-y-3">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <p className="max-w-2xl text-sm font-semibold text-[#5B6577] dark:text-[#AEB8C9]">
              Aggregate-only grant-readiness snapshot. No UID, identity, exact GPS, photos, or raw notes are returned.
            </p>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => void loadFishIQPilotMetrics()}
                disabled={fishIQPilotMetricsLoading}
                className="rounded-xl bg-[#24313A] px-3 py-2 text-xs font-black text-white shadow-sm disabled:opacity-60 dark:bg-[#FFFDF8] dark:text-[#21101A]"
              >
                {fishIQPilotMetricsLoading ? 'Loading…' : 'Refresh'}
              </button>
              <button
                type="button"
                onClick={() => {
                  if (!fishIQPilotMetrics) return;
                  downloadCsv(
                    `roddo-fishiq-pilot-privacy-safe-${makeExportStamp()}.csv`,
                    buildFishIQPilotMetricsCsv(fishIQPilotMetrics),
                  );
                }}
                disabled={!fishIQPilotMetrics || fishIQPilotMetricsLoading}
                className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-black text-emerald-800 shadow-sm disabled:opacity-50 dark:border-emerald-400/25 dark:bg-emerald-500/10 dark:text-emerald-100"
              >
                Export Safe CSV
              </button>
            </div>
          </div>

          {fishIQPilotMetricsError ? (
            <div className="rounded-2xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm font-semibold text-amber-900 dark:border-amber-400/25 dark:bg-amber-500/10 dark:text-amber-100">
              {fishIQPilotMetricsError}
            </div>
          ) : null}

          <div className="grid grid-cols-2 gap-2 md:grid-cols-3 xl:grid-cols-5">
            <StatCard title="Observations" value={pilotTotals.observations ?? '—'} />
            <StatCard title="Opt-in Aggregate" value={pilotTotals.sharedAggregateEligible ?? '—'} />
            <StatCard title="Private Only" value={pilotTotals.privateOnly ?? '—'} />
            <StatCard title="Generalized" value={pilotTotals.generalizedPrivacyProtected ?? '—'} />
            <StatCard title="Pilot Participants" value={pilotTotals.pilotParticipants ?? '—'} />
          </div>

          {fishIQPilotMetrics ? (
            <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
              {[
                ['Species reported', fishIQPilotMetrics.topSpecies],
                ['Generalized waters', fishIQPilotMetrics.topWaters],
                ['Region buckets', fishIQPilotMetrics.topRegions],
                ['Partner cohorts', fishIQPilotMetrics.partnerCodeBuckets],
                ['Water clarity', fishIQPilotMetrics.waterClarityBuckets],
                ['Catch outcomes', fishIQPilotMetrics.catchDispositionBuckets],
              ].map(([title, rows]: any) => (
                <div key={title} className="rounded-2xl border border-[#D8C4AA] bg-[#FFF8ED] p-3 dark:border-[rgba(132,151,190,.14)] dark:bg-[#0F1828]">
                  <div className="text-[11px] font-black uppercase tracking-[0.16em] text-[#6F5A44] dark:text-[#AEB8C9]">{title}</div>
                  <div className="mt-2 space-y-1">
                    {(rows || []).slice(0, 5).map((row: any) => (
                      <div key={row.label} className="flex items-center justify-between gap-3 text-xs font-bold text-[#22313A] dark:text-[#F4F6FA]">
                        <span className="truncate">{row.label}</span>
                        <span className="tabular-nums">{row.count}</span>
                      </div>
                    ))}
                    {!(rows || []).length ? <div className="text-xs font-semibold text-[#5B6577] dark:text-[#AEB8C9]">No data yet.</div> : null}
                  </div>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      </AdminPanel>

      <AdminPanel className="p-4 sm:p-5">
        <div className="space-y-3">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.18em] text-[#7B5E4C] dark:text-[#AEB8C9]">FishIQ Data Health</div>
            <h3 className="mt-1 text-lg font-bold">Catalog integrity + profile coverage</h3>
            <p className="mt-1 text-sm text-[#5B6577] dark:text-[#AEB8C9]">
              Static launch-data checks. These expose incomplete or unresolved records without showing private catches or exact spots.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-2 md:grid-cols-3 xl:grid-cols-4">
            <StatCard title="Canonical Species" value={fishIQDataHealth.totalSpecies} />
            <StatCard title="Duplicate Groups" value={fishIQDataHealth.duplicateGroups} tone={fishIQDataHealth.duplicateGroups ? 'warning' : 'success'} />
            <StatCard title="Missing Science Name" value={fishIQDataHealth.missingScientificName} tone={fishIQDataHealth.missingScientificName ? 'warning' : 'success'} />
            <StatCard title="Missing Water Realm" value={fishIQDataHealth.missingWaterRealm} tone={fishIQDataHealth.missingWaterRealm ? 'warning' : 'success'} />
            <StatCard title="Missing Habitat" value={fishIQDataHealth.missingHabitat} tone={fishIQDataHealth.missingHabitat ? 'warning' : 'success'} />
            <StatCard title="Missing Region" value={fishIQDataHealth.missingRegion} tone={fishIQDataHealth.missingRegion ? 'warning' : 'success'} />
            <StatCard title="Missing Source" value={fishIQDataHealth.missingSource} tone={fishIQDataHealth.missingSource ? 'warning' : 'success'} />
            <StatCard title="Waterbody Profiles" value={fishIQDataHealth.waterbodyProfiles} />
            <StatCard title="Established Regions" value={fishIQDataHealth.establishedRegions} />
            <StatCard title="Regions Covered" value={fishIQDataHealth.coveredRegions} />
            <StatCard title="Thin Regions" value={fishIQDataHealth.thinRegions} />
            <StatCard title="Empty Regions" value={fishIQDataHealth.emptyRegions} />
          </div>

          <div className="rounded-2xl border border-[#D8C4AA] bg-[#FFF8ED] p-3 dark:border-[rgba(132,151,190,.14)] dark:bg-[#0F1828]">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-[11px] font-black uppercase tracking-[0.16em] text-[#6F5A44] dark:text-[#AEB8C9]">Regional coverage</div>
                <p className="mt-1 text-xs font-semibold text-[#5B6577] dark:text-[#AEB8C9]">
                  Species counts are canonical catalog records enriched for each region. Thin means 1–7 records; covered means 8 or more.
                </p>
              </div>
              <span className="shrink-0 rounded-full border border-[#D8C4AA] bg-[#FFFDF8] px-2 py-1 text-[10px] font-black dark:border-[rgba(132,151,190,.14)] dark:bg-[#0C1423]">
                {fishIQDataHealth.dedicatedSpeciesPacks} dedicated packs
              </span>
            </div>
            <div className="mt-3 grid grid-cols-1 gap-2 min-[440px]:grid-cols-2 xl:grid-cols-3">
              {fishIQDataHealth.regionCoverage.map((row) => (
                <div key={row.id} className="flex items-center justify-between gap-3 rounded-xl border border-[#E5D4BE] bg-[#FFFDF8] px-3 py-2 dark:border-[rgba(132,151,190,.14)] dark:bg-[#0C1423]">
                  <div className="min-w-0">
                    <div className="truncate text-xs font-black text-[#22313A] dark:text-white">{row.label}</div>
                    <div className="text-[10px] font-bold text-[#7B5E4C] dark:text-[#AEB8C9]">{row.dedicatedSpeciesPack ? 'dedicated pack' : 'profile enrichment'}</div>
                  </div>
                  <span className={[
                    'rounded-full px-2 py-1 text-[10px] font-black tabular-nums',
                    row.status === 'covered'
                      ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-100'
                      : row.status === 'thin'
                        ? 'bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-100'
                        : 'bg-red-100 text-red-800 dark:bg-red-500/15 dark:text-red-100',
                  ].join(' ')}>
                    {row.speciesCount}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {fishIQDataHealth.unresolvedWaterbodySpeciesIds.length ? (
            <div className="rounded-2xl border border-amber-200 bg-amber-50 p-3 text-sm font-semibold text-amber-900 dark:border-amber-400/25 dark:bg-amber-500/10 dark:text-amber-100">
              <div className="font-black">Unresolved waterbody species IDs</div>
              <div className="mt-1 break-words text-xs">{fishIQDataHealth.unresolvedWaterbodySpeciesIds.join(', ')}</div>
            </div>
          ) : (
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-3 text-sm font-bold text-emerald-800 dark:border-emerald-400/25 dark:bg-emerald-500/10 dark:text-emerald-100">
              All waterbody profile species IDs resolve to the canonical catalog.
            </div>
          )}
        </div>
      </AdminPanel>

      <FishIQCalibrationPanel />

      <AnnouncementSection />
    </div>
  );
};

type AnnouncementPriority = 'normal' | 'important' | 'critical';
type AnnouncementCategory = 'service_notice' | 'maintenance' | 'safety' | 'release_note';

const priorityCopy: Record<AnnouncementPriority, { label: string; helper: string }> = {
  normal: {
    label: 'Normal',
    helper: 'Quiet app notice only. No user notification fanout.',
  },
  important: {
    label: 'Important',
    helper: 'Use for notable service updates. Notify only if the checkbox is on.',
  },
  critical: {
    label: 'Critical',
    helper: 'Urgent issue. Users will be notified automatically.',
  },
};

const AnnouncementSection: React.FC = () => {
  const [title, setTitle] = useState('');
  const [announcement, setAnnouncement] = useState('');
  const [category, setCategory] = useState<AnnouncementCategory>('service_notice');
  const [priority, setPriority] = useState<AnnouncementPriority>('normal');
  const [notifyUsers, setNotifyUsers] = useState(false);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<{ kind: 'success' | 'error'; text: string } | null>(null);

  const willNotifyUsers = priority === 'critical' || notifyUsers;
  const isCritical = priority === 'critical';
  const buttonLabel = busy
    ? 'Publishing…'
    : isCritical
      ? 'Publish Critical Notice'
      : willNotifyUsers
        ? 'Publish + Notify Users'
        : 'Publish Quiet Notice';

  const handleSend = async () => {
    const cleanTitle = title.trim();
    const msg = announcement.trim();
    if (!msg) {
      setStatus({ kind: 'error', text: 'Add a notice message first.' });
      return;
    }

    setBusy(true);
    setStatus(null);
    try {
      await cfAdminSendAnnouncement({
        title: cleanTitle || undefined,
        message: msg,
        category,
        priority,
        notifyUsers: willNotifyUsers,
      } as any);

      setStatus({
        kind: 'success',
        text: willNotifyUsers
          ? 'Notice published and user notifications queued.'
          : 'Notice published quietly. No user notifications sent.',
      });
      setTitle('');
      setAnnouncement('');
      setCategory('service_notice');
      setPriority('normal');
      setNotifyUsers(false);
    } catch (e: any) {
      console.error(e);
      setStatus({ kind: 'error', text: `Failed to publish notice: ${String(e?.message || e)}` });
    } finally {
      setBusy(false);
    }
  };

  const fieldClass =
    'roddo-mobile-form w-full rounded-2xl border border-[#D8C4AA] bg-[#FFFDF8] px-3 py-2.5 text-sm font-semibold text-[#22313A] outline-none placeholder:text-[#8A735F] focus:border-[#F1642E] focus:ring-2 focus:ring-[#F1642E]/25 dark:border-[rgba(132,151,190,.14)] dark:bg-[#0C1423] dark:text-white dark:placeholder:text-white/40 dark:focus:border-[#8A70F2] dark:focus:ring-[#8A70F2]/20';

  const modeCardClass = willNotifyUsers
    ? 'border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-400/30 dark:bg-amber-500/10 dark:text-amber-100'
    : 'border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-400/25 dark:bg-emerald-500/10 dark:text-emerald-100';

  const priorityButtonClass = (value: AnnouncementPriority) => {
    const active = priority === value;
    if (!active) {
      return 'border-[#D8C4AA] bg-[#FFFDF8] text-[#6A4C38] hover:bg-[#FFF8ED] dark:border-[rgba(132,151,190,.14)] dark:bg-[#0C1423] dark:text-[#AEB8C9] dark:hover:bg-[#0F1828]';
    }
    if (value === 'critical') return 'border-red-300 bg-red-50 text-red-800 shadow-[0_8px_18px_rgba(220,38,38,0.14)] dark:border-red-400/35 dark:bg-red-500/15 dark:text-red-100';
    if (value === 'important') return 'border-amber-300 bg-amber-50 text-amber-800 shadow-[0_8px_18px_rgba(245,158,11,0.14)] dark:border-amber-400/35 dark:bg-amber-500/15 dark:text-amber-100';
    return 'border-emerald-300 bg-emerald-50 text-emerald-800 shadow-[0_8px_18px_rgba(16,185,129,0.14)] dark:border-emerald-400/35 dark:bg-emerald-500/15 dark:text-emerald-100';
  };

  return (
    <AdminPanel className="space-y-5 px-4 py-5 sm:px-5">
      <div className="space-y-1">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-[11px] font-black uppercase tracking-[0.18em] text-[#7B5E4C] dark:text-[#AEB8C9]">
              App Notices
            </p>
            <h3 className="text-lg font-black text-[#22313A] dark:text-white">
              Publish Notice
            </h3>
          </div>
          <span
            className={[
              'rounded-full border px-3 py-1 text-[11px] font-black uppercase tracking-[0.12em]',
              willNotifyUsers
                ? 'border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-400/30 dark:bg-amber-500/10 dark:text-amber-200'
                : 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-400/25 dark:bg-emerald-500/10 dark:text-emerald-200',
            ].join(' ')}
          >
            {willNotifyUsers ? 'Notify mode' : 'Quiet mode'}
          </span>
        </div>
        <p className="text-xs font-semibold leading-5 text-[#6A4C38] dark:text-[#AEB8C9]">
          Quiet notices are saved for records. Critical notices, or notices with Notify users checked, create user notifications.
        </p>
      </div>

      <div className="space-y-3">
        <input
          value={title}
          onChange={e => setTitle(e.target.value)}
          placeholder="Optional title, e.g. Known issue: map search delays"
          className={fieldClass}
          maxLength={90}
        />

        <textarea
          value={announcement}
          onChange={e => setAnnouncement(e.target.value)}
          placeholder="Example: We are aware of an issue affecting FishIQ signals and are working on it."
          className={`${fieldClass} min-h-[128px] resize-y`}
          rows={5}
          maxLength={600}
        />
      </div>

      <div className="grid gap-4 min-[620px]:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)]">
      <label className="space-y-1 block">
        <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#7B5E4C] dark:text-[#AEB8C9]">
          Category
        </span>
        <select
          value={category}
          onChange={e => setCategory(e.target.value as AnnouncementCategory)}
          className={fieldClass}
        >
          <option value="service_notice">Service notice</option>
          <option value="maintenance">Maintenance</option>
          <option value="safety">Safety / legal</option>
          <option value="release_note">Release note</option>
        </select>
      </label>

      <div className="space-y-2">
        <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#7B5E4C] dark:text-[#AEB8C9]">
          Priority
        </span>
        <div className="grid grid-cols-3 gap-2">
          {(['normal', 'important', 'critical'] as AnnouncementPriority[]).map(value => (
            <button
              key={value}
              type="button"
              onClick={() => {
                setPriority(value);
                if (value === 'critical') setNotifyUsers(true);
                if (value === 'normal') setNotifyUsers(false);
              }}
              className={[
                'rounded-2xl border px-2.5 py-2 text-xs font-black transition',
                priorityButtonClass(value),
              ].join(' ')}
            >
              {priorityCopy[value].label}
            </button>
          ))}
        </div>
      </div>
      </div>

      <div className="grid gap-3 min-[620px]:grid-cols-2">
      <div className={["rounded-2xl border px-3 py-3 text-sm font-semibold", modeCardClass].join(' ')}>
        <div className="flex items-center justify-between gap-3">
          <div className="font-black text-[#22313A] dark:text-white">
            {willNotifyUsers ? 'User notification enabled' : `${priorityCopy[priority].label} quiet notice`}
          </div>
          <span className="rounded-full bg-white/70 px-2 py-1 text-[10px] font-black uppercase tracking-[0.12em] dark:bg-black/20">
            {willNotifyUsers ? 'Fanout' : 'No fanout'}
          </span>
        </div>
        <p className="mt-1 text-xs leading-5">
          {willNotifyUsers
            ? 'This will create notification docs for users. Use only for urgent or important notices.'
            : priorityCopy[priority].helper}
        </p>
      </div>

      <label className="flex items-start gap-3 rounded-2xl border border-[#D8C4AA] bg-[#FFFDF8] px-3 py-3 text-sm font-bold text-[#22313A] dark:border-[rgba(132,151,190,.14)] dark:bg-[#0C1423] dark:text-[#F4F6FA]">
        <input
          type="checkbox"
          checked={willNotifyUsers}
          disabled={priority === 'critical'}
          onChange={e => setNotifyUsers(e.target.checked)}
          className="mt-1 h-4 w-4 accent-[#F1642E]"
        />
        <span>
          Notify users
          <span className="block text-xs font-semibold leading-5 text-[#6A4C38] dark:text-[#AEB8C9]">
            Optional for Important notices. Forced on for Critical. Keep off for normal patch notes.
          </span>
        </span>
      </label>
      </div>

      <button
        type="button"
        onClick={handleSend}
        disabled={busy || !announcement.trim()}
        className={[
          'w-full flex items-center justify-center gap-2 rounded-xl p-3 font-black text-white shadow-[0_12px_30px_rgba(15,23,42,0.18)] transition disabled:cursor-not-allowed disabled:opacity-60',
          willNotifyUsers
            ? 'bg-[#F1642E] hover:bg-[#D94E20] dark:bg-[#FFB45B] dark:text-[#21101A] dark:hover:bg-[#F7D37D]'
            : 'bg-[#24313A] hover:bg-[#354A56] dark:bg-[#FFFDF8] dark:text-[#21101A] dark:hover:bg-[#F7D37D]',
        ].join(' ')}
      >
        <SendIcon className="w-4 h-4" />
        <span>{buttonLabel}</span>
      </button>

      {status && (
        <div
          className={[
            'rounded-2xl border px-3 py-3 text-sm font-semibold',
            status.kind === 'success'
              ? 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-400/25 dark:bg-emerald-500/10 dark:text-emerald-100'
              : 'border-red-200 bg-red-50 text-red-800 dark:border-red-400/25 dark:bg-red-500/10 dark:text-red-100',
          ].join(' ')}
        >
          {status.kind === 'success' ? '✅ ' : '❌ '}{status.text}
        </div>
      )}
    </AdminPanel>
  );
};

// ----------------------------
// USERS
// ----------------------------
const normalizeAdminAuthProviders = (anyUser: any): string[] => {
  const raw = Array.isArray(anyUser?.authProviders) && anyUser.authProviders.length
    ? anyUser.authProviders
    : Array.isArray(anyUser?.providerIds) && anyUser.providerIds.length
      ? anyUser.providerIds
      : anyUser?.providerId
        ? [anyUser.providerId]
        : anyUser?.accountType === 'guest' || anyUser?.isAnonymous === true
          ? ['anonymous']
          : [];

  const mapped = raw
    .map((value: any) => String(value || '').trim().toLowerCase())
    .filter(Boolean)
    .map((value: string) => {
      if (value === 'google.com' || value === 'google') return 'Google';
      if (value === 'apple.com' || value === 'apple') return 'Apple';
      if (value === 'password' || value === 'email' || value === 'email/password') return 'Email';
      if (value === 'anonymous' || value === 'guest') return 'Guest';
      if (value === 'phone') return 'Phone';
      return value.charAt(0).toUpperCase() + value.slice(1);
    });

    return Array.from(
      new Set<string>(mapped as string[]),
    );
};

const getAdminAccountTypeLabel = (anyUser: any): 'Guest' | 'Registered' => {
  const providers = normalizeAdminAuthProviders(anyUser);
  if (anyUser?.accountType === 'guest' || anyUser?.isAnonymous === true || providers.includes('Guest')) {
    return 'Guest';
  }
  return 'Registered';
};

const getAdminProviderSummary = (anyUser: any): string => {
  const providers = normalizeAdminAuthProviders(anyUser).filter((value) => value !== 'Guest');
  const typeLabel = getAdminAccountTypeLabel(anyUser);
  if (!providers.length) return typeLabel;
  return `${typeLabel} · ${providers.join(' + ')}`;
};


const adminAvatarInitial = (user: any): string => {
  const label = String(
    user?.displayName ||
      user?.name ||
      user?.username ||
      user?.handle ||
      '',
  ).trim();

  if (!label) return '';

  const parts = label.split(/\s+/).filter(Boolean);
  const token =
    parts.length >= 2
      ? `${parts[0]?.[0] || ''}${parts[parts.length - 1]?.[0] || ''}`
      : label.slice(0, 2);

  return token.toUpperCase();
};

const adminAvatarTone = (uid: string): string => {
  const tones = [
    'bg-sky-600',
    'bg-teal-600',
    'bg-indigo-600',
    'bg-violet-600',
    'bg-emerald-600',
    'bg-rose-600',
    'bg-amber-600',
    'bg-cyan-600',
  ];

  let hash = 0;
  for (const ch of String(uid || '')) {
    hash = ((hash << 5) - hash + ch.charCodeAt(0)) | 0;
  }

  return tones[Math.abs(hash) % tones.length];
};

const AdminMemberAvatar: React.FC<{
  user: any;
  authSummary?: AdminUserAuthSummary | null;
  sizeClass?: string;
  roundedClass?: string;
}> = ({
  user,
  authSummary,
  sizeClass = 'h-10 w-10',
  roundedClass = 'rounded-full',
}) => {
  const source = String(
    user?.avatar ||
      user?.avatarUrl ||
      user?.avatarURL ||
      user?.photoURL ||
      user?.photoUrl ||
      user?.profilePhotoUrl ||
      user?.imageUrl ||
      authSummary?.photoURL ||
      '',
  ).trim();

  const [failedSource, setFailedSource] = useState('');

  const accountType = getAdminAccountTypeLabel({
    ...user,
    providerIds: authSummary?.providerIds || user?.providerIds,
    isAnonymous: authSummary?.isAnonymous ?? user?.isAnonymous,
  });

  const initial = adminAvatarInitial(user);
  const imageUsable = Boolean(source) && failedSource !== source;

  if (imageUsable) {
    return (
      <img
        src={source}
        alt={user?.name || user?.displayName || 'Roddo member'}
        className={`${sizeClass} ${roundedClass} shrink-0 object-cover`}
        onError={() => setFailedSource(source)}
      />
    );
  }

  if (accountType === 'Registered' && initial) {
    return (
      <div
        className={`${sizeClass} ${roundedClass} ${adminAvatarTone(String(user?.uid || ''))} grid shrink-0 place-items-center border border-white/15 text-sm font-black text-white shadow-sm`}
        aria-label={`${user?.name || user?.displayName || 'Roddo member'} avatar`}
      >
        {initial}
      </div>
    );
  }

  return (
    <div
      className={`${sizeClass} ${roundedClass} grid shrink-0 place-items-center border border-[#D8C4AA] bg-[#FFFDF8] text-[#7B5E4C] dark:border-[rgba(132,151,190,.14)] dark:bg-[#0C1423] dark:text-[#AEB8C9]`}
      aria-hidden="true"
    >
      <UserIcon className="h-5 w-5" />
    </div>
  );
};

const UsersManagement: React.FC<AdminViewProps> = ({
  users,
  posts,
  userProfile,
  onUpdateUserRoles,
  onUserAndPostsDeleted,
}) => {
  const [activeRoleMenu, setActiveRoleMenu] = useState<string | null>(null);
  const [userToDelete, setUserToDelete] = useState<AppUser | null>(null);
  const [profileToView, setProfileToView] = useState<AppUser | null>(null);
  const [authSummaries, setAuthSummaries] = useState<Record<string, AdminUserAuthSummary>>({});
  const [userSearch, setUserSearch] = useState('');

  useEffect(() => {
    let cancelled = false;
    const userIds = (Object.values(users || {}) as AppUser[])
      .map((user) => String(user?.uid || '').trim())
      .filter(Boolean);

    if (!userIds.length) {
      setAuthSummaries({});
      return () => {
        cancelled = true;
      };
    }

    const load = async () => {
      try {
        const merged: Record<string, AdminUserAuthSummary> = {};
        for (let i = 0; i < userIds.length; i += 100) {
          const chunk = userIds.slice(i, i + 100);
          const res: any = await cfAdminGetUserAuthSummaries({ uids: chunk });
          Object.assign(merged, res?.data?.users || {});
        }
        if (!cancelled) setAuthSummaries(merged);
      } catch (error) {
        console.error('[Admin users] auth summaries failed', error);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [users]);

  const toggleRole = async (user: AppUser, role: 'isAdmin' | 'isSeller') => {
    const next = {
      isAdmin: user.isAdmin,
      isSeller: user.isSeller,
      [role]: !(user as any)[role],
    } as any;

    await Promise.resolve(
      onUpdateUserRoles(user.uid, {
        isAdmin: next.isAdmin,
        isSeller: next.isSeller,
      }),
    );
  };

  const filteredUsers = useMemo(() => {
    const queryText = userSearch.trim().toLowerCase();
    const list = Object.values(users || {}) as AppUser[];

    const filtered = !queryText
      ? list
      : list.filter((user) => {
          const anyUser = user as any;
          const authSummary = authSummaries[user.uid] || null;
          const haystack = [
            user.name,
            anyUser.displayName,
            anyUser.username,
            anyUser.handle,
            anyUser.email,
            authSummary?.email,
            user.uid,
            anyUser.memberNumber,
            getAdminProviderSummary({
              ...anyUser,
              providerIds: authSummary?.providerIds || anyUser.providerIds,
              isAnonymous: authSummary?.isAnonymous ?? anyUser.isAnonymous,
            }),
          ]
            .filter((value) => value !== null && value !== undefined)
            .join(' ')
            .toLowerCase();

          return haystack.includes(queryText);
        });

    return filtered.sort((a, b) => {
      const aNumber = Number((a as any).memberNumber || 0);
      const bNumber = Number((b as any).memberNumber || 0);
      if (aNumber && bNumber) return aNumber - bNumber;
      if (aNumber) return -1;
      if (bNumber) return 1;
      return String(a.name || '').localeCompare(String(b.name || ''));
    });
  }, [authSummaries, userSearch, users]);

  return (
    <>
      <div className="rounded-2xl border border-[#D8C4AA] bg-[#FFFDF8] p-3 text-[#22313A] shadow-[0_18px_50px_rgba(15,23,42,0.08)] dark:border-[rgba(132,151,190,.14)] dark:bg-[#101827] dark:text-[#F4F6FA] dark:shadow-[0_18px_60px_rgba(0,0,0,0.35)]">
        <div className="mb-3 flex flex-col gap-3 rounded-xl border border-[#E5D4BE] bg-[#FFF8ED] p-3 dark:border-[rgba(132,151,190,.14)] dark:bg-[#0F1828] sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="text-[10px] font-black uppercase tracking-[0.18em] text-[#7B5E4C] dark:text-[#AEB8C9]">Member directory</div>
            <div className="mt-1 text-sm font-black">{filteredUsers.length} of {Object.keys(users || {}).length} members</div>
          </div>
          <input
            value={userSearch}
            onChange={(event) => setUserSearch(event.target.value)}
            placeholder="Search name, email, UID, Roddo ID…"
            className="w-full rounded-xl border border-[#D8C4AA] bg-[#FFFDF8] px-3 py-2.5 text-sm font-semibold text-[#22313A] outline-none placeholder:text-[#8A735F] focus:border-[#F1642E] focus:ring-2 focus:ring-[#F1642E]/20 dark:border-[rgba(180,195,220,.14)] dark:bg-[#0C1423] dark:text-white dark:placeholder:text-[#7F8BA0] dark:focus:border-[#8A70F2] dark:focus:ring-[#8A70F2]/20 sm:max-w-sm"
          />
        </div>
        <div className="space-y-2">
        {filteredUsers.map(user => {
            const anyUser = user as any;
            const authSummary = authSummaries[user.uid] || null;
            const displayUser = authSummary
              ? {
                  ...anyUser,
                  email: authSummary.email || anyUser.email,
                  displayName: anyUser.displayName || authSummary.displayName || anyUser.name,
                  providerIds: authSummary.providerIds,
                  isAnonymous: authSummary.isAnonymous,
                  disabled: authSummary.disabled,
                }
              : anyUser;
            const memberNumber = Number(anyUser.memberNumber || 0);
            const accountTypeLabel = getAdminAccountTypeLabel(displayUser);
            const providerSummary = getAdminProviderSummary(displayUser);
            const isGuest = accountTypeLabel === 'Guest';

            return (
          <div
            key={user.uid}
            className="flex items-center justify-between p-2 rounded-xl border border-[#D8C4AA] bg-[#FFF8ED] text-[#22313A] dark:border-[rgba(132,151,190,.14)] dark:bg-[#0F1828] dark:text-[#F4F6FA]"
          >
            <div className="flex min-w-0 items-center space-x-3">
              <AdminMemberAvatar
                user={{ ...displayUser, uid: user.uid }}
                authSummary={authSummary}
              />
              <div className="min-w-0">
                <p className="truncate font-medium">{user.name}</p>
                <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] font-semibold">
                  <span className="text-[#7B5E4C] dark:text-[#AEB8C9]">
                    {memberNumber ? `Roddo ID #${memberNumber}` : 'Roddo ID pending'}
                  </span>
                  <span className="text-[#5B6577] dark:text-[#CFC2F4]">
                    {providerSummary}
                  </span>
                  {isGuest && (
                    <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[9px] font-black uppercase tracking-[0.08em] text-amber-800 dark:bg-amber-500/15 dark:text-amber-100">
                      Guest
                    </span>
                  )}
                  {user.isAdmin && <span className="font-semibold text-indigo-700 dark:text-indigo-300">Admin</span>}
                  {user.isSeller && <span className="font-semibold text-[#3A4B54] dark:text-white">Seller</span>}
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-1">
              <button
                type="button"
                onClick={() => setProfileToView(user)}
                className="rounded-full p-2 text-[#314653] transition hover:bg-black/5 dark:text-[#F4F6FA] dark:hover:bg-[#0F1828]"
                title="View profile"
                aria-label="View profile"
              >
                <UserIcon className="h-5 w-5" />
              </button>

              <div className="relative">
                <button
                  type="button"
                  onClick={() => setActiveRoleMenu(activeRoleMenu === user.uid ? null : user.uid)}
                  className="p-2 hover:bg-black/5 dark:hover:bg-[#0F1828] rounded-full"
                  disabled={user.uid === userProfile.uid}
                >
                  <MoreHorizontalIcon className="w-5 h-5" />
                </button>

                {activeRoleMenu === user.uid && (
                  <div className="absolute right-0 mt-2 w-48 rounded-xl border border-black/10 bg-[#FFFDF8] shadow-lg dark:border-[rgba(132,151,190,.14)] dark:bg-[#0C1423] z-30 p-2 space-y-1">
                    <p className="text-xs font-bold px-2 pb-1 border-b dark:border-[rgba(132,151,190,.14)]">
                      Set Roles
                    </p>

                    <button
                      type="button"
                      onClick={() => toggleRole(user, 'isAdmin')}
                      className="w-full text-left flex items-center justify-between px-2 py-1 text-sm hover:bg-black/5 dark:hover:bg-[#0F1828] rounded-md"
                    >
                      <div className="flex items-center space-x-2">
                        <ShieldIcon className="w-4 h-4" />
                        <span>Admin</span>
                      </div>
                      <div
                        className={`w-4 h-4 rounded border ${
                          user.isAdmin ? 'bg-[#f1642e] border-primary' : 'border-black/20 dark:border-[#6F5EA8]'
                        }`}
                      />
                    </button>

                    <button
                      type="button"
                      onClick={() => toggleRole(user, 'isSeller')}
                      className="w-full text-left flex items-center justify-between px-2 py-1 text-sm hover:bg-black/5 dark:hover:bg-[#0F1828] rounded-md"
                    >
                      <div className="flex items-center space-x-2">
                        <MarketplaceIcon className="w-4 h-4" />
                        <span>Seller</span>
                      </div>
                      <div
                        className={`w-4 h-4 rounded border ${
                          user.isSeller ? 'bg-[#f1642e] border-primary' : 'border-black/20 dark:border-[#6F5EA8]'
                        }`}
                      />
                    </button>
                  </div>
                )}
              </div>

              <button
                type="button"
                onClick={() => setUserToDelete(user)}
                className="p-2 text-rose-600 dark:text-rose-300 hover:bg-red-100 dark:hover:bg-red-900/50 rounded-full"
                disabled={user.uid === userProfile.uid}
              >
                <TrashIcon className="w-5 h-5" />
              </button>
            </div>
          </div>
            );
          })}
        </div>
      </div>

      <AdminUserProfileModal
        user={profileToView}
        authSummary={profileToView ? authSummaries[profileToView.uid] || null : null}
        posts={posts}
        isCurrentUser={profileToView?.uid === userProfile.uid}
        onClose={() => setProfileToView(null)}
        onDelete={(user) => setUserToDelete(user)}
      />

      <ConfirmationModal
        isOpen={!!userToDelete}
        onClose={() => setUserToDelete(null)}
        onConfirm={async () => {
          if (!userToDelete) return;
          await Promise.resolve(onUserAndPostsDeleted(userToDelete.uid));
          setUserToDelete(null);
        }}
        title="Delete user + content?"
        message="This will permanently delete the user and associated content. This cannot be undone."
        confirmText="Delete"
      />
    </>
  );
};


const getAdminPostAuthorId = (post: any) =>
  String(
    post?.authorId ||
      post?.userId ||
      post?.uid ||
      post?.ownerId ||
      post?.createdBy ||
      post?.author?.uid ||
      post?.user?.uid ||
      '',
  ).trim();

const getAdminNumber = (...values: any[]) => {
  for (const value of values) {
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return 0;
};

const AdminUserProfileModal: React.FC<{
  user: AppUser | null;
  authSummary?: AdminUserAuthSummary | null;
  posts: CommunityPost[];
  isCurrentUser: boolean;
  onClose: () => void;
  onDelete: (user: AppUser) => void;
}> = ({ user, authSummary, posts, isCurrentUser, onClose, onDelete }) => {
  const [liveSnapshot, setLiveSnapshot] = useState<any | null>(null);
  const [liveLoading, setLiveLoading] = useState(false);
  const [liveError, setLiveError] = useState<string | null>(null);
  const [grantShells, setGrantShells] = useState('');
  const [grantSearchLures, setGrantSearchLures] = useState('');
  const [grantReason, setGrantReason] = useState('QA replenishment');
  const [grantBusy, setGrantBusy] = useState(false);
  const [grantStatus, setGrantStatus] = useState<string | null>(null);
  const { confirm, confirmModal } = useAdminConfirmation();

  useEffect(() => {
    let cancelled = false;
    if (!user?.uid) {
      setLiveSnapshot(null);
      setLiveError(null);
      setLiveLoading(false);
      return () => {
        cancelled = true;
      };
    }

    setLiveSnapshot(null);
    setLiveError(null);
    setLiveLoading(true);

    void cfAdminGetUserAdminSnapshot({ uid: user.uid })
      .then((res: any) => {
        if (!cancelled) setLiveSnapshot(res?.data || null);
      })
      .catch((error: any) => {
        console.error('[Admin user profile] live snapshot failed', error);
        if (!cancelled) setLiveError(error?.message || 'Unable to load live account data.');
      })
      .finally(() => {
        if (!cancelled) setLiveLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [user?.uid]);

  if (!user) return null;

  const liveAuth = liveSnapshot?.auth || authSummary || null;
  const liveProfile = liveSnapshot?.profile || {};
  const anyUser: any = {
    ...user,
    ...liveProfile,
    email: liveAuth?.email || (user as any).email,
    providerIds: liveAuth?.providerIds || (user as any).providerIds,
    isAnonymous: liveAuth?.isAnonymous ?? (user as any).isAnonymous,
    disabled: liveAuth?.disabled ?? (user as any).disabled,
  };
  const userPosts = (Array.isArray(posts) ? posts : []).filter((post: any) => getAdminPostAuthorId(post) === user.uid);
  const displayName = anyUser.displayName || anyUser.name || anyUser.username || 'Unnamed angler';
  const handle = anyUser.handle || anyUser.username || '';
  const avatar = String(anyUser.avatar || anyUser.photoURL || anyUser.avatarUrl || '').trim();
  const email = anyUser.email || anyUser.authEmail || anyUser.privateEmail || '';
  const accessStatus = anyUser.accessStatus || anyUser.status || 'unknown';
  const memberNumber = Number(anyUser.memberNumber || 0);
  const accountTypeLabel = getAdminAccountTypeLabel(anyUser);
  const isGuest = accountTypeLabel === 'Guest';
  const providerLabel = normalizeAdminAuthProviders(anyUser).join(', ') || (isGuest ? 'Guest' : 'Unknown');
  const providerSummary = getAdminProviderSummary(anyUser);
  const joined = anyUser.createdAt || anyUser.joinedAt || anyUser.updatedAt || liveAuth?.creationTime;
  const joinedLabel =
    typeof joined?.toDate === 'function'
      ? joined.toDate().toLocaleString()
      : typeof joined?.toMillis === 'function'
        ? new Date(joined.toMillis()).toLocaleString()
        : typeof joined === 'number'
          ? new Date(joined).toLocaleString()
          : typeof joined === 'string' && joined
            ? new Date(joined).toLocaleString()
            : 'Unknown';
  const lastSignInLabel = liveAuth?.lastSignInTime
    ? new Date(liveAuth.lastSignInTime).toLocaleString()
    : 'Unknown';

  const liveMetrics = liveSnapshot?.metrics || {};
  const catches = Number.isFinite(Number(liveMetrics.catches))
    ? Number(liveMetrics.catches)
    : getAdminNumber(anyUser.stats?.catches, anyUser.stats?.catchCount, anyUser.catchCount, anyUser.catchesCount);
  const species = Number.isFinite(Number(liveMetrics.species))
    ? Number(liveMetrics.species)
    : getAdminNumber(anyUser.stats?.species, anyUser.stats?.speciesCount, anyUser.speciesCount);
  const waters = Number.isFinite(Number(liveMetrics.waters))
    ? Number(liveMetrics.waters)
    : getAdminNumber(anyUser.stats?.waters, anyUser.stats?.waterCount, anyUser.waterCount, anyUser.savedSpotCount);
  const livePostsCount = Number.isFinite(Number(liveMetrics.posts)) ? Number(liveMetrics.posts) : userPosts.length;
  const xp = getAdminNumber(anyUser.xp, anyUser.stats?.xp, anyUser.progression?.xp);
  const level = getAdminNumber(anyUser.level, anyUser.stats?.level, anyUser.progression?.level);
  const shellBalance = Math.max(0, Math.floor(getAdminNumber(anyUser.shells, anyUser.shellBalance)));
  const searchLureBalance = Math.max(0, Math.floor(getAdminNumber(anyUser.searchLures)));

  const runConsumableGrant = async () => {
    const shells = Math.max(0, Math.floor(Number(grantShells || 0) || 0));
    const searchLures = Math.max(0, Math.floor(Number(grantSearchLures || 0) || 0));
    const reason = grantReason.trim();

    if (!shells && !searchLures) {
      setGrantStatus('Enter a Shell or Search Lure amount first.');
      return;
    }
    if (reason.length < 4) {
      setGrantStatus('Add a short reason for the grant.');
      return;
    }

    const ok = await confirm({
      title: `Grant consumables to ${displayName}?`,
      message: `This server-authoritative grant will add ${shells} Shells and ${searchLures} Search Lures. Reason: ${reason}`,
      confirmText: 'Grant',
    });
    if (!ok) return;

    setGrantBusy(true);
    setGrantStatus(null);
    try {
      const requestId = `admin_grant_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
      const res: any = await cfAdminGrantConsumables({
        userId: user.uid,
        shells,
        searchLures,
        reason,
        requestId,
      });
      const data = res?.data || {};
      setLiveSnapshot((prev: any) => ({
        ...(prev || {}),
        profile: {
          ...(prev?.profile || {}),
          shells: Number(data.shellsAfter ?? shellBalance),
          searchLures: Number(data.searchLuresAfter ?? searchLureBalance),
        },
      }));
      setGrantShells('');
      setGrantSearchLures('');
      setGrantStatus(`Granted. New balance: ${Number(data.shellsAfter || 0)} Shells · ${Number(data.searchLuresAfter || 0)} Search Lures.`);
    } catch (err: any) {
      console.error('[Admin consumable grant] failed', err);
      setGrantStatus(`Grant failed: ${String(err?.message || err)}`);
    } finally {
      setGrantBusy(false);
    }
  };

  const modal = (
    <div
      className="roddo-admin-mobile-modal absolute inset-x-0 z-[24000] flex items-center justify-center bg-black/55 px-2 py-3 backdrop-blur-md sm:px-4"
      style={{
        top: 'calc(var(--roddo-header-h, 72px) + 8px)',
        bottom: 'calc(var(--roddo-nav-h, 84px) + 8px)',
      }}
      role="presentation"
      onClick={onClose}
    >
      <div
        className="flex h-full max-h-full w-full max-w-3xl flex-col overflow-hidden rounded-[22px] border border-[#D8C4AA] bg-[#FFFDF8] text-[#22313A] shadow-[0_30px_90px_rgba(0,0,0,0.34)] dark:border-[rgba(180,195,220,.14)] dark:bg-[#0C1423] dark:text-[#F4F6FA]"
        role="dialog"
        aria-modal="true"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4 [-webkit-overflow-scrolling:touch] sm:px-5">

        <div className="sticky top-0 z-20 -mx-4 mb-4 flex items-start justify-between gap-3 border-b border-[#D8C4AA] bg-[#FFFDF8]/96 px-4 pb-4 pt-1 backdrop-blur-md dark:border-[rgba(132,151,190,.14)] dark:bg-[#0C1423]/96 sm:-mx-5 sm:px-5">
          <div className="flex min-w-0 items-center gap-3">
            <AdminMemberAvatar
              user={{ ...anyUser, uid: user.uid, avatar }}
              authSummary={liveAuth}
              sizeClass="h-14 w-14"
              roundedClass="rounded-2xl"
            />
            <div className="min-w-0">
              <div className="text-[11px] font-black uppercase tracking-[0.18em] text-[#7B5E4C] dark:text-[#AEB8C9]">User profile</div>
              <h3 className="truncate text-xl font-black">{displayName}</h3>
              <p className="truncate text-xs font-semibold text-[#5B6577] dark:text-[#AEB8C9]">
                {handle ? `@${handle}` : 'No handle'} {email ? `• ${email}` : ''}
              </p>
            </div>
          </div>
          <button type="button" onClick={onClose} className="grid h-11 w-11 shrink-0 place-items-center rounded-full border border-[#D8C4AA] bg-[#FFF8ED] text-[#22313A] shadow-sm dark:border-[rgba(132,151,190,.14)] dark:bg-[#0F1828] dark:text-[#F4F6FA]" aria-label="Close profile">
            <XIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
          <StatCard title="Posts" value={livePostsCount} />
          <StatCard title="Catches" value={catches} />
          <StatCard title="Species" value={species} />
          <StatCard title="Waters" value={waters} />
        </div>

        {liveLoading ? (
          <div className="mt-3 rounded-xl border border-[#D8C4AA] bg-[#FFF8ED] px-3 py-2 text-xs font-bold text-[#5B6577] dark:border-[rgba(132,151,190,.14)] dark:bg-[#101827] dark:text-[#AEB8C9]">
            Loading live Firebase account + catch data…
          </div>
        ) : liveError ? (
          <div className="mt-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs font-bold text-red-700 dark:border-red-400/30 dark:bg-[#3A1720] dark:text-red-200">
            {liveError}
          </div>
        ) : null}

        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <div className="rounded-2xl border border-[#D8C4AA] bg-[#FFF8ED] p-3 dark:border-[rgba(132,151,190,.14)] dark:bg-[#101827]">
            <div className="text-[11px] font-black uppercase tracking-[0.14em] text-[#7B5E4C] dark:text-[#AEB8C9]">Account</div>
            <div className="mt-2 space-y-1 text-sm font-semibold">
              <p className="text-base font-black">
                {memberNumber ? `Roddo ID #${memberNumber}` : 'Roddo ID pending'}
              </p>
              <p>Account: {accountTypeLabel}</p>
              <p>Sign-in: {providerSummary}</p>
              <p>Status: {liveAuth?.disabled ? 'disabled' : String(accessStatus === 'unknown' && liveAuth ? 'active' : accessStatus)}</p>
              <p>Providers: {providerLabel}</p>
              <p>Joined: {joinedLabel}</p>
              <p>Last sign-in: {lastSignInLabel}</p>
              <p>UID: <span className="break-all font-mono text-xs">{user.uid}</span></p>
            </div>
          </div>

          <div className="rounded-2xl border border-[#D8C4AA] bg-[#FFF8ED] p-3 dark:border-[rgba(132,151,190,.14)] dark:bg-[#101827]">
            <div className="text-[11px] font-black uppercase tracking-[0.14em] text-[#7B5E4C] dark:text-[#AEB8C9]">Roles + progress</div>
            <div className="mt-2 flex flex-wrap gap-2 text-xs font-black">
              {anyUser.isAdmin ? <span className="rounded-full bg-indigo-100 px-2 py-1 text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-200">Admin</span> : null}
              {anyUser.isSeller ? <span className="rounded-full bg-amber-100 px-2 py-1 text-amber-800 dark:bg-amber-500/20 dark:text-amber-100">Seller</span> : null}
              <span className="rounded-full bg-emerald-100 px-2 py-1 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-100">Level {level || 1}</span>
              <span className="rounded-full bg-sky-100 px-2 py-1 text-sky-700 dark:bg-sky-500/20 dark:text-sky-100">{xp} XP</span>
            </div>
          </div>
        </div>

        <div className="mt-4 rounded-2xl border border-[#D8C4AA] bg-[#FFF8ED] p-3 dark:border-[rgba(132,151,190,.14)] dark:bg-[#101827]">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="text-[11px] font-black uppercase tracking-[0.14em] text-[#7B5E4C] dark:text-[#AEB8C9]">Economy admin</div>
              <div className="mt-1 text-sm font-black">Replenish consumables</div>
              <p className="mt-1 text-xs font-semibold text-[#5B6577] dark:text-[#AEB8C9]">Server-authoritative grant with per-user ledgers and an admin audit record. Admin Search Lure grants may exceed the free regen cap.</p>
            </div>
            <div className="flex gap-2">
              <span className="rounded-full bg-amber-100 px-2.5 py-1 text-xs font-black text-amber-800 dark:bg-amber-500/15 dark:text-amber-100">{shellBalance} Shells</span>
              <span className="rounded-full bg-sky-100 px-2.5 py-1 text-xs font-black text-sky-800 dark:bg-sky-500/15 dark:text-sky-100">{searchLureBalance} Lures</span>
            </div>
          </div>

          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <div>
              <label className="text-[11px] font-black uppercase tracking-[0.12em] text-[#7B5E4C] dark:text-[#AEB8C9]">Search Lures</label>
              <div className="mt-1 flex gap-2">
                {[10, 25, 50].map((value) => (
                  <button key={value} type="button" disabled={grantBusy} onClick={() => setGrantSearchLures(String(value))} className="rounded-lg border border-sky-200 bg-sky-50 px-2.5 py-1.5 text-xs font-black text-sky-800 dark:border-sky-400/25 dark:bg-sky-500/10 dark:text-sky-100">+{value}</button>
                ))}
              </div>
              <input type="number" min={0} step={1} value={grantSearchLures} onChange={(e) => setGrantSearchLures(e.target.value)} placeholder="Custom amount" className="mt-2 w-full rounded-xl border border-[#D8C4AA] bg-[#FFFDF8] px-3 py-2 text-sm font-semibold outline-none dark:border-[rgba(132,151,190,.14)] dark:bg-[#0C1423]" />
            </div>
            <div>
              <label className="text-[11px] font-black uppercase tracking-[0.12em] text-[#7B5E4C] dark:text-[#AEB8C9]">Shells</label>
              <div className="mt-1 flex gap-2">
                {[100, 500, 1000].map((value) => (
                  <button key={value} type="button" disabled={grantBusy} onClick={() => setGrantShells(String(value))} className="rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-xs font-black text-amber-800 dark:border-amber-400/25 dark:bg-amber-500/10 dark:text-amber-100">+{value}</button>
                ))}
              </div>
              <input type="number" min={0} step={1} value={grantShells} onChange={(e) => setGrantShells(e.target.value)} placeholder="Custom amount" className="mt-2 w-full rounded-xl border border-[#D8C4AA] bg-[#FFFDF8] px-3 py-2 text-sm font-semibold outline-none dark:border-[rgba(132,151,190,.14)] dark:bg-[#0C1423]" />
            </div>
          </div>

          <label className="mt-3 block">
            <span className="text-[11px] font-black uppercase tracking-[0.12em] text-[#7B5E4C] dark:text-[#AEB8C9]">Reason</span>
            <input value={grantReason} onChange={(e) => setGrantReason(e.target.value)} maxLength={240} className="mt-1 w-full rounded-xl border border-[#D8C4AA] bg-[#FFFDF8] px-3 py-2 text-sm font-semibold outline-none dark:border-[rgba(132,151,190,.14)] dark:bg-[#0C1423]" />
          </label>

          <button type="button" disabled={grantBusy || (!Number(grantShells || 0) && !Number(grantSearchLures || 0))} onClick={() => void runConsumableGrant()} className="mt-3 w-full rounded-xl border border-[#1F5E8C] bg-[#286F9F] px-4 py-2.5 text-sm font-black text-white shadow-sm disabled:cursor-not-allowed disabled:opacity-50 dark:border-[#8EA2FF]/55 dark:bg-[#8A70F2]">
            {grantBusy ? 'Granting…' : 'Grant Consumables'}
          </button>

          {grantStatus ? <div className="mt-2 rounded-xl border border-[#D8C4AA] bg-[#FFFDF8] px-3 py-2 text-xs font-bold dark:border-[rgba(132,151,190,.14)] dark:bg-[#0C1423]">{grantStatus}</div> : null}
        </div>

        {Array.isArray(liveSnapshot?.recentCatches) && liveSnapshot.recentCatches.length ? (
          <div className="mt-4 rounded-2xl border border-[#D8C4AA] bg-[#FFF8ED] p-3 dark:border-[rgba(132,151,190,.14)] dark:bg-[#101827]">
            <div className="text-[11px] font-black uppercase tracking-[0.14em] text-[#7B5E4C] dark:text-[#AEB8C9]">Recent catches</div>
            <div className="mt-2 space-y-2">
              {liveSnapshot.recentCatches.map((catchRow: any) => (
                <div key={catchRow.id} className="flex items-center gap-3 rounded-xl border border-[#D8C4AA] bg-[#FFFDF8] p-2 text-sm dark:border-[rgba(132,151,190,.14)] dark:bg-[#0C1423]">
                  {catchRow.imageUrl ? (
                    <img src={catchRow.imageUrl} alt={catchRow.species || 'Catch'} className="h-10 w-10 shrink-0 rounded-lg object-cover" />
                  ) : null}
                  <div className="min-w-0">
                    <p className="truncate font-black">{catchRow.species || 'Unknown catch'}</p>
                    <p className="truncate text-[11px] text-[#7B5E4C] dark:text-[#AEB8C9]">
                      {catchRow.water || 'Unknown water'}
                      {catchRow.timestamp ? ` • ${new Date(catchRow.timestamp).toLocaleString()}` : ''}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        <div className="mt-4 rounded-2xl border border-[#D8C4AA] bg-[#FFF8ED] p-3 dark:border-[rgba(132,151,190,.14)] dark:bg-[#101827]">
          <div className="text-[11px] font-black uppercase tracking-[0.14em] text-[#7B5E4C] dark:text-[#AEB8C9]">Recent posts</div>
          {(Array.isArray(liveSnapshot?.recentPosts) ? liveSnapshot.recentPosts : userPosts).length === 0 ? (
            <p className="mt-2 text-sm font-semibold text-[#5B6577] dark:text-[#AEB8C9]">No visible posts found for this user.</p>
          ) : (
            <div className="mt-2 space-y-2">
              {(Array.isArray(liveSnapshot?.recentPosts) ? liveSnapshot.recentPosts : userPosts).slice(0, 5).map((post: any) => (
                <div key={post.id || post.postId} className="rounded-xl border border-[#D8C4AA] bg-[#FFFDF8] p-2 text-sm dark:border-[rgba(132,151,190,.14)] dark:bg-[#0C1423]">
                  <p className="line-clamp-2 font-semibold">{post.caption || post.text || post.body || post.content || 'Post with media/metadata'}</p>
                  <p className="mt-1 text-[11px] text-[#7B5E4C] dark:text-[#AEB8C9]">{post.id || post.postId || 'unknown post id'}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-4 flex flex-wrap justify-end gap-2">
          <button
            type="button"
            onClick={async () => {
              try {
                const res: any = await cfAdminGrantFoundingMemberRewards({ userId: user.uid });
                const data = res?.data || {};
                window.alert(data.granted ? 'Founding rewards granted.' : `No grant needed: ${data.reason || 'already granted or not eligible'}.`);
              } catch (err: any) {
                window.alert(`Founding reward grant failed: ${String(err?.message || err)}`);
              }
            }}
            className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-2 text-sm font-black text-amber-800 dark:border-amber-400/40 dark:bg-amber-500/15 dark:text-amber-100"
          >
            Grant Founding Rewards
          </button>
          <button type="button" onClick={onClose} className="rounded-xl border border-[#D8C4AA] bg-[#FFFDF8] px-4 py-2 text-sm font-black dark:border-[rgba(132,151,190,.14)] dark:bg-[#0F1828]">Close</button>
          <button
            type="button"
            disabled={isCurrentUser}
            onClick={() => {
              onClose();
              onDelete(user);
            }}
            className="rounded-xl border border-red-200 bg-red-50 px-4 py-2 text-sm font-black text-red-700 disabled:cursor-not-allowed disabled:opacity-50 dark:border-red-400/40 dark:bg-[#3A1720] dark:text-red-100"
          >
            Delete user
          </button>
        </div>

        </div>
        {confirmModal}
      </div>
    </div>
  );

  if (typeof document === 'undefined') return null;

  return createPortal(
    modal,
    document.getElementById('roddo-app-shell') || document.body,
  );
};

// ----------------------------
// POSTS
// ----------------------------
// ----------------------------
// MARKETPLACE
// ----------------------------
const MarketplaceManagement: React.FC<AdminViewProps> = ({ marketplaceItems, onItemDeleted, currency }) => {
  const { t, i18n } = useTranslation();
  const [itemToDelete, setItemToDelete] = useState<MarketplaceItem | null>(null);
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditQuery, setAuditQuery] = useState('');
  const [auditEvents, setAuditEvents] = useState<BazaarAuditEvent[]>([]);
  const [auditError, setAuditError] = useState<string | null>(null);

  const loadBazaarAudit = useCallback(async (queryText = auditQuery) => {
    setAuditLoading(true);
    setAuditError(null);
    try {
      const res = await cfAdminGetBazaarAuditEvents({ query: queryText.trim() || undefined, limit: 50 });
      setAuditEvents(Array.isArray(res?.data?.events) ? res.data.events : []);
    } catch (error: any) {
      console.error('[Admin Bazaar audit] failed', error);
      setAuditError(error?.message || 'Unable to load Bazaar activity.');
    } finally {
      setAuditLoading(false);
    }
  }, [auditQuery]);

  const toggleBazaarAudit = () => {
    setAuditOpen((open) => {
      const next = !open;
      if (next && auditEvents.length === 0 && !auditLoading) void loadBazaarAudit();
      return next;
    });
  };

  const itemList = Object.values(marketplaceItems || {}) as MarketplaceItem[];

  const getFirstImage = (item: any): string | null => {
    if (Array.isArray(item?.imageUrls) && item.imageUrls[0]) return item.imageUrls[0];
    if (Array.isArray(item?.images) && item.images[0]) return item.images[0];
    if (typeof item?.imageUrl === 'string' && item.imageUrl) return item.imageUrl;
    if (typeof item?.image === 'string' && item.image) return item.image;
    return null;
  };

  const getName = (item: any) => item?.title || item?.name || 'Untitled';

  const getMarketplaceDocId = (item: any): string =>
    String(
      item?.docId ||
        item?.docKey ||
        item?.firestoreId ||
        item?.listingId ||
        item?.itemId ||
        item?.id ||
        '',
    ).trim();

  const getPriceCents = (item: any) => {
    if (typeof item?.priceCents === 'number') return item.priceCents;
    if (typeof item?.price === 'number') return Math.round(item.price * 100);
    return 0;
  };

  return (
    <>
      <div className="mb-3 rounded-2xl border border-amber-300 bg-amber-50 px-3 py-3 text-xs font-semibold text-amber-900 dark:border-amber-400/25 dark:bg-amber-500/10 dark:text-amber-100">
        <div className="font-black">Bazaar rollout</div>
        <div className="mt-1">Classified listings are live. Roddo Checkout, seller payouts, and shipping automation remain disabled until the Stripe release gate is intentionally opened.</div>
      </div>
      <div className="mb-3 overflow-hidden rounded-2xl border border-[#D8C4AA] bg-[#FFFDF8] text-[#22313A] shadow-sm dark:border-[rgba(132,151,190,.14)] dark:bg-[#101827] dark:text-[#F4F6FA]">
        <button type="button" onClick={toggleBazaarAudit} className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition hover:bg-[#FFF8ED] dark:hover:bg-[#0F1828]">
          <div className="min-w-0">
            <div className="text-xs font-black uppercase tracking-[0.14em] text-[#7B5E4C] dark:text-[#AEB8C9]">Bazaar activity log</div>
            <div className="mt-1 text-sm font-semibold">Conversation, offer, cleanup, and moderation references</div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {auditEvents.length > 0 ? <span className="rounded-full bg-[#286F9F]/10 px-2 py-1 text-[10px] font-black text-[#286F9F] dark:bg-sky-400/10 dark:text-sky-200">{auditEvents.length}</span> : null}
            <span className="text-lg font-black">{auditOpen ? '−' : '+'}</span>
          </div>
        </button>
        {auditOpen ? (
          <div className="border-t border-[#D8C4AA] p-3 dark:border-[rgba(132,151,190,.14)]">
            <div className="flex flex-col gap-2 sm:flex-row">
              <input value={auditQuery} onChange={(e) => setAuditQuery(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') void loadBazaarAudit(); }} placeholder="Search thread, listing, UID, message, offer…" className="min-w-0 flex-1 rounded-xl border border-[#D8C4AA] bg-white px-3 py-2 text-xs font-semibold outline-none focus:border-[#286F9F] dark:border-[rgba(180,195,220,.16)] dark:bg-[#0C1423]" />
              <button type="button" disabled={auditLoading} onClick={() => void loadBazaarAudit()} className="rounded-xl border border-[#D8C4AA] bg-[#FFF8ED] px-3 py-2 text-xs font-black transition active:scale-[.98] disabled:opacity-50 dark:border-[rgba(180,195,220,.16)] dark:bg-[#0F1828]">{auditLoading ? 'Loading…' : 'Search'}</button>
            </div>
            {auditError ? <div className="mt-2 rounded-xl bg-rose-500/10 px-3 py-2 text-xs font-semibold text-rose-700 dark:text-rose-200">{auditError}</div> : null}
            <div className="mt-3 max-h-[360px] space-y-2 overflow-y-auto pr-1 roddo-content-scroll">
              {!auditLoading && auditEvents.length === 0 ? <div className="rounded-xl border border-dashed border-[#D8C4AA] px-3 py-3 text-xs font-semibold text-[#7B5E4C] dark:border-[rgba(180,195,220,.16)] dark:text-[#AEB8C9]">No matching Bazaar activity yet.</div> : null}
              {auditEvents.map((event) => (
                <details key={event.id} className="group rounded-xl border border-[#D8C4AA] bg-[#FFF8ED] px-3 py-2 dark:border-[rgba(132,151,190,.14)] dark:bg-[#0F1828]">
                  <summary className="cursor-pointer list-none">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="truncate text-[11px] font-black uppercase tracking-[.08em]">{String(event.type || 'bazaar_event').replaceAll('_', ' ')}</div>
                        <div className="mt-1 truncate text-[10px] font-semibold text-[#7B5E4C] dark:text-[#AEB8C9]">{event.listingId ? `Listing ${event.listingId}` : event.threadId ? `Thread ${event.threadId}` : 'Bazaar system event'}</div>
                      </div>
                      <div className="shrink-0 text-[10px] font-bold text-[#7B5E4C] dark:text-[#AEB8C9]">{event.createdAtMs ? new Date(Number(event.createdAtMs)).toLocaleString() : '—'}</div>
                    </div>
                  </summary>
                  <div className="mt-2 grid gap-1 border-t border-[#D8C4AA] pt-2 text-[10px] font-semibold dark:border-[rgba(132,151,190,.14)]">
                    {event.threadId ? <div><b>Thread:</b> {event.threadId}</div> : null}
                    {event.messageId ? <div><b>Message:</b> {event.messageId}</div> : null}
                    {event.offerId ? <div><b>Offer:</b> {event.offerId}</div> : null}
                    {event.actorUid ? <div><b>Actor:</b> {event.actorUid}</div> : null}
                    {event.buyerId ? <div><b>Buyer:</b> {event.buyerId}</div> : null}
                    {event.sellerId ? <div><b>Seller:</b> {event.sellerId}</div> : null}
                    {event.messageSnapshot ? <div className="mt-1 rounded-lg bg-black/5 p-2 whitespace-pre-wrap break-words dark:bg-white/5"><b>Evidence:</b> {String(event.messageSnapshot)}</div> : null}
                    {event.beforeSnapshot ? <div className="mt-1 rounded-lg bg-black/5 p-2 whitespace-pre-wrap break-words dark:bg-white/5"><b>Before:</b> {String(event.beforeSnapshot)}</div> : null}
                    {event.afterSnapshot ? <div className="rounded-lg bg-black/5 p-2 whitespace-pre-wrap break-words dark:bg-white/5"><b>After:</b> {String(event.afterSnapshot)}</div> : null}
                  </div>
                </details>
              ))}
            </div>
          </div>
        ) : null}
      </div>

      <div className="space-y-2 rounded-2xl border border-[#D8C4AA] bg-[#FFFDF8] p-3 text-[#22313A] shadow-[0_18px_50px_rgba(15,23,42,0.08)] dark:border-[rgba(132,151,190,.14)] dark:bg-[#101827] dark:text-[#F4F6FA] dark:shadow-[0_18px_60px_rgba(0,0,0,0.35)]">
        {itemList.length > 0 ? (
          itemList.map((item: any) => {
            const img = getFirstImage(item);
            const name = getName(item);
            const priceCents = getPriceCents(item);

            return (
              <div
                key={String(item.id)}
                className="flex items-center justify-between p-2 rounded-xl border border-[#D8C4AA] bg-[#FFF8ED] text-[#22313A] dark:border-[rgba(132,151,190,.14)] dark:bg-[#0F1828] dark:text-[#F4F6FA]"
              >
                <div className="flex items-center space-x-3 overflow-hidden">
                  {img && (
                    <img
                      src={img}
                      alt={name}
                      className="w-10 h-10 rounded-md object-cover flex-shrink-0"
                    />
                  )}

                  <div className="truncate">
                    <p className="font-medium truncate">{name}</p>
                    <p className="text-xs text-[#7B5E4C] dark:text-[#AEB8C9]">
                      by {item.sellerName || '—'} — {formatCurrency(priceCents / 100, currency, i18n.language)}
                    </p>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => setItemToDelete(item)}
                  className="p-2 text-rose-600 dark:text-rose-300 hover:bg-red-100 dark:hover:bg-red-900/50 rounded-full flex-shrink-0 ml-2"
                  aria-label="Delete item"
                >
                  <TrashIcon className="w-5 h-5" />
                </button>
              </div>
            );
          })
        ) : (
          <p className="py-4 text-center text-sm font-semibold text-[#5B6577] dark:text-[#F4F6FA]">No marketplace items to manage.</p>
        )}
      </div>

      <ConfirmationModal
        isOpen={!!itemToDelete}
        onClose={() => setItemToDelete(null)}
        onConfirm={async () => {
          if (!itemToDelete) return;
          const itemId = getMarketplaceDocId(itemToDelete);
          if (!itemId) throw new Error('This listing is missing its Firestore document id.');
          await Promise.resolve(onItemDeleted(itemId));
          setItemToDelete(null);
        }}
        title={t('bazaar.deleteTitle', 'Delete Listing')}
        message={t(
          'app.confirmDeleteListing',
          'This will permanently remove this listing from the marketplace. This action cannot be undone.',
        )}
        confirmText={t('common.delete', 'Delete')}
      />
    </>
  );
};

// ----------------------------
// TOURNAMENT REVIEW + ROLLOUT
// ----------------------------
function TournamentManagement(props: AdminViewProps) {
  const [platformConfig, setPlatformConfig] =
    useState<TournamentPlatformConfig>(DEFAULT_TOURNAMENT_CONFIG);
  const [configBusy, setConfigBusy] = useState(false);
  const [configMsg, setConfigMsg] = useState<string | null>(null);
  const [reviewTournament, setReviewTournament] = useState<Tournament | null>(null);
  const [reviewAction, setReviewAction] = useState<
    | 'details'
    | 'approve'
    | 'changes_requested'
    | 'reject'
    | 'reopen_for_changes'
    | 'cancel'
    | 'archive'
  >('details');
  const [reviewNotes, setReviewNotes] = useState('');
  const [reviewBusy, setReviewBusy] = useState(false);
  const [reviewMsg, setReviewMsg] = useState<string | null>(null);
  const { confirm, confirmModal } = useAdminConfirmation();

  useEffect(() => subscribeTournamentConfig(setPlatformConfig), []);

  const saveTournamentConfig = async (
    patch: Partial<TournamentPlatformConfig>,
  ) => {
    setConfigBusy(true);
    setConfigMsg(null);
    try {
      await cfAdminSetTournamentConfig(patch);
      setConfigMsg('Tournament rollout controls updated.');
    } catch (error: any) {
      console.error(error);
      setConfigMsg(
        `Could not update tournament controls: ${String(error?.message || error)}`,
      );
    } finally {
      setConfigBusy(false);
    }
  };

  const {
    tournaments,
    onTournamentDelete,
  } = props;

  const openReview = (
    tournament: Tournament,
    action:
      | 'details'
      | 'approve'
      | 'changes_requested'
      | 'reject'
      | 'reopen_for_changes'
      | 'cancel'
      | 'archive' = 'details',
  ) => {
    setReviewTournament(tournament);
    setReviewAction(action);
    setReviewNotes(String(tournament.reviewNotes || ''));
    setReviewMsg(null);
  };

  const closeReview = () => {
    if (reviewBusy) return;
    setReviewTournament(null);
    setReviewAction('details');
    setReviewNotes('');
    setReviewMsg(null);
  };

  const handleDeleteTournament = async (tournamentId: string) => {
    const ok = await confirm({
      title: 'Delete tournament?',
      message:
        'This will permanently remove this tournament. This cannot be undone.',
      confirmText: 'Delete',
    });
    if (!ok) return;

    await Promise.resolve(onTournamentDelete(String(tournamentId)));
    if (reviewTournament?.id === tournamentId) closeReview();
  };

  const submitReview = async () => {
    if (!reviewTournament) return;
    if (reviewAction === 'details') return;

    const notes = reviewNotes.trim();
    if (
      (
        reviewAction === 'changes_requested' ||
        reviewAction === 'reject' ||
        reviewAction === 'cancel' ||
        reviewAction === 'archive'
      ) &&
      notes.length < 3
    ) {
      setReviewMsg('Add at least 3 characters of review notes before continuing.');
      return;
    }

    const actionCopy = {
      approve: {
        title: 'Approve tournament?',
        message:
          'This locks the approved rules and moves the tournament into the approved queue. Registration, tournament start/end, and catch submissions will follow this tournament’s own saved schedule and settings.',
        confirmText: 'Approve',
      },
      changes_requested: {
        title: 'Return tournament for changes?',
        message:
          'This returns the tournament to an editable draft and sends your notes to the director.',
        confirmText: 'Return',
      },
      reject: {
        title: 'Reject tournament?',
        message:
          'This records the rejection reason and moves the tournament into the rejected queue.',
        confirmText: 'Reject',
      },
      reopen_for_changes: {
        title: 'Reopen tournament for changes?',
        message:
          'This unlocks the approved rules and returns the tournament to an editable changes-requested draft.',
        confirmText: 'Reopen',
      },
      cancel: {
        title: 'Cancel tournament?',
        message:
          'This closes the tournament lifecycle. Registration and submissions remain unavailable.',
        confirmText: 'Cancel tournament',
      },
      archive: {
        title: 'Archive tournament?',
        message:
          'This moves the closed tournament into history while preserving its audit records.',
        confirmText: 'Archive',
      },
    } as const;

    const selectedAction = actionCopy[reviewAction];

    const confirmed = await confirm(selectedAction);
    if (!confirmed) return;

    setReviewBusy(true);
    setReviewMsg(null);
    try {
      if (
        reviewAction === 'approve' ||
        reviewAction === 'changes_requested' ||
        reviewAction === 'reject'
      ) {
        await adminReviewTournament(
          reviewTournament.id,
          reviewAction,
          notes,
        );
      } else {
        await adminTournamentLifecycle(
          reviewTournament.id,
          reviewAction,
          notes,
        );
      }

      setReviewMsg('Tournament lifecycle updated.');

      window.setTimeout(() => {
        setReviewTournament(null);
        setReviewAction('details');
        setReviewNotes('');
        setReviewMsg(null);
      }, 650);
    } catch (error: any) {
      console.error('[Admin tournament review] failed', error);
      setReviewMsg(
        error?.message || 'Tournament review action failed.',
      );
    } finally {
      setReviewBusy(false);
    }
  };

  const tournamentList = Object.values(tournaments || {}) as Tournament[];

  const rolloutPanel = (
    <AdminPanel className="p-4 sm:p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-lg font-black">Tournament access</h3>
          <p className="mt-1 text-sm text-[#5B6577] dark:text-[#AEB8C9]">
            Open or pause tournaments without changing app code.
          </p>
        </div>
        <span
          className={[
            'rounded-full border px-3 py-1 text-[10px] font-black uppercase tracking-[0.12em]',
            platformConfig.publicAccessEnabled
              ? 'border-emerald-300 bg-emerald-50 text-emerald-800 dark:border-emerald-400/25 dark:bg-emerald-500/10 dark:text-emerald-100'
              : 'border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-400/25 dark:bg-amber-500/10 dark:text-amber-100',
          ].join(' ')}
        >
          {platformConfig.publicAccessEnabled ? 'Open' : 'Coming soon'}
        </span>
      </div>

      <label className="mt-4 flex cursor-pointer items-center justify-between gap-4 rounded-2xl border border-[#8A70F2]/25 bg-[#8A70F2]/[0.06] p-4 dark:border-[#8A70F2]/35 dark:bg-[#8A70F2]/[0.12]">
        <div className="min-w-0">
          <div className="font-black">Open tournaments to users</div>
          <div className="mt-1 text-xs font-semibold text-[#5B6577] dark:text-[#AEB8C9]">
            Off shows Coming Soon to regular users. Admin preview can remain available for testing.
          </div>
        </div>
        <input
          type="checkbox"
          checked={platformConfig.publicAccessEnabled}
          disabled={configBusy}
          onChange={(event) =>
            void saveTournamentConfig({ publicAccessEnabled: event.target.checked })
          }
          className="h-5 w-5 shrink-0 accent-[#8A70F2]"
        />
      </label>

      <div className="mt-4 border-t border-[#D8C4AA] pt-4 dark:border-[rgba(132,151,190,.14)]">
        <div className="mb-2 text-[11px] font-black uppercase tracking-[0.16em] text-[#7B5E4C] dark:text-[#AEB8C9]">
          Feature controls
        </div>
        <div className="grid gap-2 sm:grid-cols-2">
          {[
            ['adminPreviewEnabled', 'Admin preview', 'Admins can enter while public access is closed.'],
            ['creationEnabled', 'Tournament creation', 'Allow members to create tournament drafts.'],
            ['registrationEnabled', 'Participant registration', 'Allow scheduled registration and participant joins.'],
            ['shellEntryEnabled', 'Shell entry fees', 'Allow paid tournament registration using Shells.'],
            ['submissionEnabled', 'Catch submissions', 'Allow approved participants to submit tournament catches.'],
          ].map(([key, label, hint]) => (
            <label
              key={key}
              className="flex cursor-pointer items-center justify-between gap-3 rounded-xl border border-[#D8C4AA] bg-[#FFFDF8] p-3 dark:border-[rgba(132,151,190,.14)] dark:bg-[#0C1423]"
            >
              <div className="min-w-0">
                <div className="text-sm font-black">{label}</div>
                <div className="mt-0.5 text-[11px] font-semibold text-[#6A4C38] dark:text-[#7F8BA0]">
                  {hint}
                </div>
              </div>
              <input
                type="checkbox"
                checked={Boolean((platformConfig as any)[key])}
                disabled={configBusy}
                onChange={(event) =>
                  void saveTournamentConfig({
                    [key]: event.target.checked,
                  } as any)
                }
                className="h-5 w-5 shrink-0 accent-[#8A70F2]"
              />
            </label>
          ))}
        </div>
      </div>

      <div className="mt-3 rounded-xl border border-sky-200 bg-sky-50/70 p-3 text-xs font-semibold leading-relaxed text-slate-700 dark:border-sky-400/20 dark:bg-sky-500/[0.08] dark:text-slate-200">
        Individual tournaments still keep their own visibility, schedule, entry fee, evidence, scoring, and participant settings. Roddo Official remains admin-only.
      </div>

      {configMsg ? (
        <p className="mt-3 text-sm font-bold">{configMsg}</p>
      ) : null}
    </AdminPanel>
  );

  const renderTournamentGroup = (title: string, data: Tournament[]) => (
    <div key={title}>
      <h3 className="mb-2 text-lg font-black text-[#22313A] dark:text-white">
        {title}
      </h3>

      {data.length ? (
        <div className="space-y-3">
          {data.map((tour) => {
            const status = String(tour.status || 'draft');
            const approvalStatus = String(
              tour.approvalStatus || 'not_submitted',
            );
            const pending =
              status === 'pending_approval' ||
              approvalStatus === 'pending';
            const endDate = Number(tour.endDate || 0);
            const isExpired = endDate > 0 && endDate <= Date.now();

            return (
              <AdminPanel key={tour.id} className="p-4">
                <div className="flex flex-col gap-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="truncate text-base font-black">
                          {tour.name}
                        </p>
                        <span className="rounded-full border px-2 py-1 text-[10px] font-black uppercase tracking-[0.12em]">
                          {status.replaceAll('_', ' ')}
                        </span>
                        <span className="rounded-full border border-violet-300 bg-violet-50 px-2 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-violet-800 dark:bg-violet-500/10 dark:text-violet-200">
                          {approvalStatus.replaceAll('_', ' ')}
                        </span>
                        {isExpired ? (
                          <span className="rounded-full border border-slate-300 bg-slate-100 px-2 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-slate-700 dark:border-slate-500/40 dark:bg-slate-500/10 dark:text-slate-200">
                            Expired
                          </span>
                        ) : null}
                      </div>

                      <p className="mt-1 text-xs font-semibold text-[#7B5E4C] dark:text-[#AEB8C9]">
                        {tour.directorName || 'Unknown director'}
                        {' • '}
                        {tour.locationLabel || tour.location || 'No location'}
                        {typeof tour.startDate === 'number'
                          ? ` • ${new Date(tour.startDate).toLocaleDateString()}`
                          : ''}
                      </p>

                      <p className="mt-2 line-clamp-2 text-sm font-semibold text-[#5B6577] dark:text-[#F4F6FA]">
                        {tour.description || 'No description provided.'}
                      </p>

                      {tour.reviewNotes ? (
                        <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-900 dark:border-amber-400/25 dark:bg-amber-500/10 dark:text-amber-100">
                          Last review note: {tour.reviewNotes}
                        </div>
                      ) : null}
                    </div>

                    <button
                      type="button"
                      onClick={() => handleDeleteTournament(tour.id)}
                      className="shrink-0 rounded-xl p-2 text-red-600 hover:bg-red-50 dark:text-red-300 dark:hover:bg-red-500/10"
                      title="Delete tournament"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => openReview(tour, 'details')}
                      className="rounded-xl border border-[#D8C4AA] bg-[#FFFDF8] px-3 py-2 text-xs font-black dark:border-[rgba(132,151,190,.14)] dark:bg-[#0C1423]"
                    >
                      View details
                    </button>

                    {status === 'approved' &&
                    !isExpired ? (
                      <button
                        type="button"
                        onClick={async () => {
                          const ok = await confirm({
                            title: 'Open tournament registration?',
                            message:
                              'Eligible signed-in anglers will be able to register until registration is closed or the configured deadline passes.',
                            confirmText: 'Open registration',
                          });
                          if (!ok) return;

                          try {
                            setConfigMsg(null);
                            await adminSetTournamentRegistration(tour.id, 'open');
                            setConfigMsg(`Registration opened for ${tour.name}.`);
                          } catch (error: any) {
                            console.error('[Admin tournament registration] open failed', error);
                            setConfigMsg(
                              error?.message || 'Could not open registration.',
                            );
                          }
                        }}
                        className="rounded-xl border border-emerald-300 bg-emerald-50 px-3 py-2 text-xs font-black text-emerald-800 dark:border-emerald-400/30 dark:bg-emerald-500/10 dark:text-emerald-100"
                      >
                        Open registration
                      </button>
                    ) : null}

                    {status === 'registration_open' && !isExpired ? (
                      <button
                        type="button"
                        onClick={async () => {
                          const ok = await confirm({
                            title: 'Close tournament registration?',
                            message:
                              'New registrations will stop. Existing participant records will remain saved.',
                            confirmText: 'Close registration',
                          });
                          if (!ok) return;

                          try {
                            setConfigMsg(null);
                            await adminSetTournamentRegistration(tour.id, 'close');
                            setConfigMsg(`Registration closed for ${tour.name}.`);
                          } catch (error: any) {
                            console.error('[Admin tournament registration] close failed', error);
                            setConfigMsg(
                              error?.message || 'Could not close registration.',
                            );
                          }
                        }}
                        className="rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 text-xs font-black text-amber-800 dark:border-amber-400/30 dark:bg-amber-500/10 dark:text-amber-100"
                      >
                        Close registration
                      </button>
                    ) : null}

                    {pending ? (
                      <>
                        <button
                          type="button"
                          onClick={() => openReview(tour, 'approve')}
                          className="rounded-xl border border-emerald-300 bg-emerald-50 px-3 py-2 text-xs font-black text-emerald-800 dark:border-emerald-400/30 dark:bg-emerald-500/10 dark:text-emerald-100"
                        >
                          Approve
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            openReview(tour, 'changes_requested')
                          }
                          className="rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 text-xs font-black text-amber-800 dark:border-amber-400/30 dark:bg-amber-500/10 dark:text-amber-100"
                        >
                          Return for changes
                        </button>
                        <button
                          type="button"
                          onClick={() => openReview(tour, 'reject')}
                          className="rounded-xl border border-red-300 bg-red-50 px-3 py-2 text-xs font-black text-red-800 dark:border-red-400/30 dark:bg-red-500/10 dark:text-red-100"
                        >
                          Reject
                        </button>
                      </>
                    ) : null}

                    {['approved', 'rejected'].includes(status) && !isExpired ? (
                      <button
                        type="button"
                        onClick={() => openReview(tour, 'reopen_for_changes')}
                        className="rounded-xl border border-amber-300 px-3 py-2 text-xs font-black text-amber-800 dark:text-amber-100"
                      >
                        Reopen for changes
                      </button>
                    ) : null}

                    {!isExpired && !['cancelled', 'archived', 'finalized', 'completed'].includes(status) ? (
                      <button
                        type="button"
                        onClick={() => openReview(tour, 'cancel')}
                        className="rounded-xl border border-red-300 px-3 py-2 text-xs font-black text-red-800 dark:text-red-100"
                      >
                        Cancel
                      </button>
                    ) : null}

                    {['rejected', 'cancelled', 'finalized', 'completed'].includes(status) ? (
                      <button
                        type="button"
                        onClick={() => openReview(tour, 'archive')}
                        className="rounded-xl border px-3 py-2 text-xs font-black"
                      >
                        Archive
                      </button>
                    ) : null}
                  </div>
                </div>
              </AdminPanel>
            );
          })}
        </div>
      ) : (
        <p className="text-sm font-semibold text-[#5B6577] dark:text-[#F4F6FA]">
          No {title.toLowerCase()} tournaments.
        </p>
      )}
    </div>
  );

  const pendingReviewCount = tournamentList.filter(
    (tour) => tour.status === 'pending_approval' || tour.approvalStatus === 'pending',
  ).length;
  const pendingReviewRows = tournamentList
    .filter((tour) => tour.status === 'pending_approval' || tour.approvalStatus === 'pending')
    .sort((a, b) => Number(a.submittedAt || a.updatedAt || 0) - Number(b.submittedAt || b.updatedAt || 0));
  const liveTournamentCount = tournamentList.filter((tour) =>
    ['active', 'live'].includes(String(tour.status || '')),
  ).length;

  return (
    <>
      <div className="space-y-4">
        <div className="flex flex-col gap-3 rounded-xl border border-[#B9CCDA] bg-[#EEF6FB] px-4 py-3 sm:flex-row sm:items-center sm:justify-between dark:border-[#526487]/45 dark:bg-[#152239]">
          <div>
            <div className="text-[10px] font-black uppercase tracking-[0.18em] text-[#41677F] dark:text-[#9EACBF]">Tournament operations</div>
            <div className="mt-1 text-lg font-black text-[#22313A] dark:text-white">Approval + lifecycle queue</div>
            <p className="mt-1 text-xs font-semibold text-[#5B6577] dark:text-[#AEB8C9]">Keep this queue clear while tournament hosting still requires Roddo approval.</p>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2">
            {pendingReviewRows.length ? (
              <button
                type="button"
                onClick={() => openReview(pendingReviewRows[0], 'details')}
                className="rounded-lg border border-[#1F5E8C] bg-[#286F9F] px-3 py-2 text-xs font-black text-white shadow-sm hover:bg-[#225F88] dark:border-[#8EA2FF]/50 dark:bg-[#8A70F2]"
              >
                Review next pending
              </button>
            ) : null}
            <div className="grid grid-cols-2 gap-2 sm:flex sm:items-center">
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-center dark:border-amber-400/20 dark:bg-amber-500/10">
              <div className="text-[9px] font-black uppercase tracking-[0.12em] text-amber-700 dark:text-amber-200">Pending</div>
              <div className="text-xl font-black tabular-nums text-[#22313A] dark:text-white">{pendingReviewCount}</div>
            </div>
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-center dark:border-emerald-400/20 dark:bg-emerald-500/10">
              <div className="text-[9px] font-black uppercase tracking-[0.12em] text-emerald-700 dark:text-emerald-200">Live</div>
              <div className="text-xl font-black tabular-nums text-[#22313A] dark:text-white">{liveTournamentCount}</div>
            </div>
            </div>
          </div>
        </div>

        {rolloutPanel}

        <div className="space-y-4 p-1">
          {renderTournamentGroup(
            'Pending approval',
            pendingReviewRows,
          )}

          {renderTournamentGroup(
            'Changes requested',
            tournamentList.filter(
              (tour) => tour.approvalStatus === 'changes_requested',
            ),
          )}

          {renderTournamentGroup(
            'Private drafts',
            tournamentList.filter(
              (tour) =>
                tour.status === 'draft' &&
                tour.approvalStatus !== 'changes_requested',
            ),
          )}

          {renderTournamentGroup(
            'Approved / upcoming',
            tournamentList.filter((tour) => {
              const endDate = Number(tour.endDate || 0);
              const expired = endDate > 0 && endDate <= Date.now();
              return (
                !expired &&
                ['approved', 'registration_open', 'upcoming'].includes(
                  String(tour.status || ''),
                )
              );
            }),
          )}

          {renderTournamentGroup(
            'Active',
            tournamentList.filter((tour) => {
              const endDate = Number(tour.endDate || 0);
              const expired = endDate > 0 && endDate <= Date.now();
              return (
                !expired &&
                ['active', 'live'].includes(String(tour.status || ''))
              );
            }),
          )}

          {renderTournamentGroup(
            'Completed / expired',
            tournamentList.filter((tour) => {
              const status = String(tour.status || '');
              const endDate = Number(tour.endDate || 0);
              const expired = endDate > 0 && endDate <= Date.now();
              return (
                ['submissions_closed', 'under_review', 'provisional_results', 'completed', 'finalized', 'archived'].includes(status) ||
                (expired && ['approved', 'registration_open', 'upcoming', 'active', 'live'].includes(status))
              );
            }),
          )}
        </div>
      </div>

      {reviewTournament && typeof document !== 'undefined'
        ? createPortal(
        <div
          className="roddo-admin-mobile-modal absolute inset-x-0 z-[20000] flex items-center justify-center bg-black/60 p-2 backdrop-blur-sm sm:p-3"
          style={{
            top: 'calc(var(--roddo-header-h, 72px) + 8px)',
            bottom: 'calc(var(--roddo-nav-h, 84px) + 8px)',
          }}
          onClick={closeReview}
          role="presentation"
        >
          <div
            className="flex h-[calc(100%-12px)] max-h-[calc(100%-12px)] w-[calc(100%-12px)] max-w-[calc(100%-12px)] flex-col overflow-hidden rounded-[22px] border border-[#D8C4AA] bg-[#FFFDF8] text-[#22313A] shadow-[0_28px_90px_rgba(0,0,0,0.30)] dark:border-[rgba(132,151,190,.14)] dark:bg-[#0C1423] dark:text-white"
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="admin-tournament-review-title"
          >
            <div className="flex shrink-0 items-start justify-between gap-3 border-b border-[#D8C4AA] px-4 py-3 dark:border-[rgba(132,151,190,.14)]">
              <div>
                <div className="text-[11px] font-black uppercase tracking-[0.18em] text-violet-700 dark:text-violet-200">
                  Tournament review
                </div>
                <h3 id="admin-tournament-review-title" className="mt-1 text-xl font-black sm:text-2xl">
                  {reviewTournament.name}
                </h3>
                <p className="mt-1 text-sm font-semibold text-[#5B6577] dark:text-[#AEB8C9]">
                  {reviewTournament.directorName || 'Unknown director'}
                  {' • '}
                  {String(reviewTournament.status || 'draft').replaceAll('_', ' ')}
                </p>
              </div>

              <button
                type="button"
                onClick={closeReview}
                className="rounded-full border p-2"
                aria-label="Close review"
              >
                <XIcon className="h-5 w-5" />
              </button>
            </div>

            <div
              className="min-h-0 flex-1 touch-pan-y overflow-y-auto overscroll-contain px-4 py-3 [-webkit-overflow-scrolling:touch]"
              style={{ WebkitOverflowScrolling: 'touch' }}
            >
            <div className="grid gap-2 sm:grid-cols-2 sm:gap-3">
              {[
                ['Tier', reviewTournament.tier || 'community'],
                ['Participation', reviewTournament.participationMode || 'solo'],
                ['Visibility', reviewTournament.visibility || 'private'],
                ['Scoring', reviewTournament.scoringMode || 'longest'],
                ['Location', reviewTournament.locationLabel || reviewTournament.location || '—'],
                ['Timezone', reviewTournament.timezone || 'UTC'],
                ['Max participants', reviewTournament.maxParticipants ?? '—'],
                ['Entry fee', `${reviewTournament.entryFeeShells || 0} Shells`],
                ['Participant approval', reviewTournament.participantApprovalRequired ? 'Required' : 'Automatic'],
                ['Tournament submissions', reviewTournament.verifiedSubmissionsEnabled === false ? 'Off' : 'On'],
                [
                  'Evidence source',
                  reviewTournament.submissionCapturePolicy === 'live_or_upload'
                    ? 'Live + uploaded photos'
                    : 'Live capture only',
                ],
                ['Capture mode', reviewTournament.captureMode || 'live_photos'],
                ['Verification', reviewTournament.verificationLevel || 'standard'],
                ['Tournament code', reviewTournament.tournamentCodeRequired === false ? 'Not required' : 'Required'],
                ['GPS verification', reviewTournament.locationVerification || 'approximate'],
                ['Catch disposition', reviewTournament.catchDisposition || 'either'],
                ['Eligible species', (reviewTournament.eligibleSpecies || []).join(', ') || 'Any'],
                [
                  'Registration opens',
                  reviewTournament.registrationOpensAt
                    ? new Date(Number(reviewTournament.registrationOpensAt)).toLocaleString()
                    : '—',
                ],
                [
                  'Registration closes',
                  reviewTournament.registrationClosesAt
                    ? new Date(Number(reviewTournament.registrationClosesAt)).toLocaleString()
                    : '—',
                ],
                [
                  'Tournament starts',
                  reviewTournament.startDate
                    ? new Date(Number(reviewTournament.startDate)).toLocaleString()
                    : '—',
                ],
                [
                  'Tournament ends',
                  reviewTournament.endDate
                    ? new Date(Number(reviewTournament.endDate)).toLocaleString()
                    : '—',
                ],
              ].map(([label, value]) => (
                <div
                  key={String(label)}
                  className="rounded-2xl border border-[#D8C4AA] bg-[#FFF8ED] p-3 dark:border-[rgba(132,151,190,.14)] dark:bg-[#101827]"
                >
                  <div className="text-[10px] font-black uppercase tracking-[0.14em] text-[#7B5E4C] dark:text-[#AEB8C9]">
                    {label}
                  </div>
                  <div className="mt-1 break-words text-sm font-bold">
                    {String(value).replaceAll('_', ' ')}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-4 space-y-3">
              <div className="rounded-2xl border border-[#D8C4AA] bg-[#FFF8ED] p-3 dark:border-[rgba(132,151,190,.14)] dark:bg-[#101827]">
                <div className="text-[10px] font-black uppercase tracking-[0.14em] text-[#7B5E4C] dark:text-[#AEB8C9]">
                  Description
                </div>
                <div className="mt-1 whitespace-pre-wrap text-sm font-semibold">
                  {reviewTournament.description || 'No description.'}
                </div>
              </div>

              <div className="rounded-2xl border border-[#D8C4AA] bg-[#FFF8ED] p-3 dark:border-[rgba(132,151,190,.14)] dark:bg-[#101827]">
                <div className="text-[10px] font-black uppercase tracking-[0.14em] text-[#7B5E4C] dark:text-[#AEB8C9]">
                  Rules
                </div>
                <div className="mt-1 whitespace-pre-wrap text-sm font-semibold">
                  {reviewTournament.rulesText ||
                    reviewTournament.rules ||
                    'No rules provided.'}
                </div>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3">
              {String(reviewTournament.status || '') === 'pending_approval' ? (
                <>
              <button
                type="button"
                onClick={() => setReviewAction('approve')}
                className={[
                  'rounded-xl border px-3 py-2 text-xs font-black',
                  reviewAction === 'approve'
                    ? 'border-emerald-500 bg-emerald-500 text-white'
                    : 'border-emerald-300 bg-emerald-50 text-emerald-800',
                ].join(' ')}
              >
                Approve
              </button>
              <button
                type="button"
                onClick={() => setReviewAction('changes_requested')}
                className={[
                  'rounded-xl border px-3 py-2 text-xs font-black',
                  reviewAction === 'changes_requested'
                    ? 'border-amber-500 bg-amber-500 text-white'
                    : 'border-amber-300 bg-amber-50 text-amber-800',
                ].join(' ')}
              >
                Return
              </button>
              <button
                type="button"
                onClick={() => setReviewAction('reject')}
                className={[
                  'rounded-xl border px-3 py-2 text-xs font-black',
                  reviewAction === 'reject'
                    ? 'border-red-500 bg-red-500 text-white'
                    : 'border-red-300 bg-red-50 text-red-800',
                ].join(' ')}
              >
                Reject
              </button>
                </>
              ) : null}

              {['approved', 'rejected'].includes(String(reviewTournament.status || '')) ? (
                <button
                  type="button"
                  onClick={() => setReviewAction('reopen_for_changes')}
                  className={[
                    'rounded-xl border px-3 py-2 text-xs font-black transition',
                    reviewAction === 'reopen_for_changes'
                      ? 'border-amber-500 bg-amber-500 text-white shadow-sm'
                      : 'border-amber-300 bg-amber-50 text-amber-800',
                  ].join(' ')}
                >
                  Reopen for changes
                </button>
              ) : null}

              {!['cancelled', 'archived', 'finalized', 'completed'].includes(String(reviewTournament.status || '')) ? (
                <button
                  type="button"
                  onClick={() => setReviewAction('cancel')}
                  className={[
                    'rounded-xl border px-3 py-2 text-xs font-black transition',
                    reviewAction === 'cancel'
                      ? 'border-red-500 bg-red-500 text-white shadow-sm'
                      : 'border-red-300 bg-red-50 text-red-800',
                  ].join(' ')}
                >
                  Cancel
                </button>
              ) : null}

              {['rejected', 'cancelled', 'finalized', 'completed'].includes(String(reviewTournament.status || '')) ? (
                <button
                  type="button"
                  onClick={() => setReviewAction('archive')}
                  className={[
                    'rounded-xl border px-3 py-2 text-xs font-black transition',
                    reviewAction === 'archive'
                      ? 'border-slate-700 bg-slate-700 text-white shadow-sm'
                      : 'border-slate-300 bg-slate-50 text-slate-800',
                  ].join(' ')}
                >
                  Archive
                </button>
              ) : null}
            </div>

            <label className="mt-4 block">
              <span className="text-[11px] font-black uppercase tracking-[0.14em] text-[#7B5E4C] dark:text-[#AEB8C9]">
                Review notes
              </span>
              <textarea
                value={reviewNotes}
                onChange={(event) => setReviewNotes(event.target.value)}
                rows={5}
                maxLength={4000}
                placeholder={
                  reviewAction === 'approve'
                    ? 'Optional approval note'
                    : reviewAction === 'reopen_for_changes'
                      ? 'Optional: explain what should be adjusted before resubmission'
                      : reviewAction === 'cancel'
                        ? 'Required: explain why this tournament is being cancelled'
                        : reviewAction === 'archive'
                          ? 'Required: explain why this tournament is being archived'
                          : 'Required: explain what must change or why it was rejected'
                }
                className="mt-2 w-full rounded-2xl border border-[#D8C4AA] bg-[#FFFDF8] p-3 text-sm font-semibold outline-none focus:ring-2 focus:ring-violet-400/30 dark:border-[rgba(132,151,190,.14)] dark:bg-[#0C1423]"
              />
            </label>

            {reviewMsg ? (
              <div className="mt-3 rounded-xl border border-[#D8C4AA] bg-[#FFF8ED] px-3 py-2 text-sm font-bold dark:border-[rgba(132,151,190,.14)] dark:bg-[#101827]">
                {reviewMsg}
              </div>
            ) : null}

            </div>

            <div className="flex shrink-0 gap-2 border-t border-[#D8C4AA] bg-[#FFFDF8]/95 px-3 py-3 backdrop-blur-md dark:border-[rgba(132,151,190,.14)] dark:bg-[#0C1423]/95">
              <button
                type="button"
                onClick={closeReview}
                disabled={reviewBusy}
                className="flex-1 rounded-xl border px-4 py-3 text-sm font-black disabled:opacity-50"
              >
                Close
              </button>
              <button
                type="button"
                onClick={submitReview}
                disabled={
                  reviewBusy ||
                  reviewAction === 'details'
                }
                className="flex-1 rounded-xl bg-violet-600 px-4 py-3 text-sm font-black text-white disabled:opacity-45"
              >
                {reviewBusy
                  ? 'Saving…'
                  : reviewAction === 'approve'
                    ? 'Approve tournament'
                    : reviewAction === 'changes_requested'
                      ? 'Return for changes'
                      : reviewAction === 'reject'
                        ? 'Reject tournament'
                        : reviewAction === 'reopen_for_changes'
                          ? 'Reopen for changes'
                          : reviewAction === 'cancel'
                            ? 'Cancel tournament'
                            : reviewAction === 'archive'
                              ? 'Archive tournament'
                              : 'Choose an action'}
              </button>
            </div>
          </div>
        </div>,
        document.getElementById('roddo-app-shell') || document.body,
      )
        : null}

      <div className="relative z-[22000]">{confirmModal}</div>
    </>
  );
}

// ----------------------------
// TOURNAMENT MODAL
// ----------------------------
const TournamentModal: React.FC<{
  tournament: Tournament | null;
  onSave: (data: Partial<Tournament> & { name: string }) => void;
  onClose: () => void;
  onTournamentDelete: (tournamentId: string) => void;
  currency: string;
}> = ({ tournament, onSave, onClose, onTournamentDelete, currency }) => {
  const { t } = useTranslation();

  const [name, setName] = useState((tournament as any)?.name ?? '');
  const [description, setDescription] = useState((tournament as any)?.description ?? '');
  const [location, setLocation] = useState((tournament as any)?.location ?? '');
  const [startTime, setStartTime] = useState(
    (tournament as any)?.startDate ? new Date((tournament as any).startDate).toISOString().slice(0, 16) : '',
  );
  const [endTime, setEndTime] = useState(
    (tournament as any)?.endDate ? new Date((tournament as any).endDate).toISOString().slice(0, 16) : '',
  );
  const [rules, setRules] = useState((tournament as any)?.rules ?? '');
  const [prizePool, setPrizePool] = useState(
    (tournament as any)?.prizePool !== undefined ? String((tournament as any).prizePool) : '',
  );

  const handleSubmit = () => {
    onSave({
      name,
      description,
      location,
      rules,
      startDate: startTime ? new Date(startTime).getTime() : undefined,
      endDate: endTime ? new Date(endTime).getTime() : undefined,
      prizePool: prizePool !== '' ? parseFloat(prizePool) || 0 : undefined,
    });
  };

  return (
    <div className="roddo-admin-mobile-modal fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-3 pb-[max(12px,env(safe-area-inset-bottom))] pt-[max(12px,env(safe-area-inset-top))] sm:p-4">
      <div className="w-full max-w-lg space-y-4 overflow-y-auto rounded-[30px] border border-black/10 bg-[linear-gradient(180deg,rgba(255,252,248,0.98)_0%,rgba(245,239,232,0.96)_100%)] p-5 shadow-[0_24px_60px_rgba(89,65,37,0.18)] dark:border-[rgba(132,151,190,.14)] dark:bg-[linear-gradient(180deg,rgba(32,28,61,0.98)_0%,rgba(19,16,39,0.98)_100%)] dark:shadow-[0_24px_60px_rgba(0,0,0,0.45)]" style={{ maxHeight: 'calc(var(--roddo-visual-vh, 100dvh) - max(24px, env(safe-area-inset-top)) - max(24px, env(safe-area-inset-bottom)))', WebkitOverflowScrolling: 'touch' }}>
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold">
            {tournament ? t('tournaments.edit', 'Edit Tournament') : t('tournaments.create', 'Create Tournament')}
          </h2>

          <div className="flex items-center gap-2">
            {!!tournament?.id && (
              <button
                type="button"
                onClick={() => onTournamentDelete(String((tournament as any).id))}
                title="Delete tournament"
                className="p-2 rounded-xl hover:bg-red-50 text-red-600"
              >
                <TrashIcon className="w-4 h-4" />
              </button>
            )}

            <button
              type="button"
              onClick={onClose}
              className="p-2 rounded-xl hover:bg-black/5 dark:hover:bg-[#0F1828]"
              title="Close"
            >
              <XIcon className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="space-y-3">
          <input
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder={t('tournaments.namePlaceholder', 'Tournament Name')}
            className="w-full rounded-2xl border border-black/10 bg-[#FFFDF8] px-3 py-2 text-sm text-[#22313A] dark:border-[rgba(132,151,190,.14)] dark:bg-[#0C1423] dark:text-white"
          />

          <textarea
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder={t('tournaments.descriptionPlaceholder', 'Description')}
            className="w-full rounded-2xl border border-black/10 bg-[#FFFDF8] px-3 py-2 text-sm text-[#22313A] dark:border-[rgba(132,151,190,.14)] dark:bg-[#0C1423] dark:text-white"
            rows={2}
          />

          <input
            type="text"
            value={location}
            onChange={e => setLocation(e.target.value)}
            placeholder={t('tournaments.locationPlaceholder', 'Location')}
            className="w-full rounded-2xl border border-black/10 bg-[#FFFDF8] px-3 py-2 text-sm text-[#22313A] dark:border-[rgba(132,151,190,.14)] dark:bg-[#0C1423] dark:text-white"
          />

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs">{t('tournaments.startTime', 'Start Time')}</label>
              <input
                type="datetime-local"
                value={startTime}
                onChange={e => setStartTime(e.target.value)}
                className="w-full rounded-2xl border border-black/10 bg-[#FFFDF8] px-3 py-2 text-sm text-[#22313A] dark:border-[rgba(132,151,190,.14)] dark:bg-[#0C1423] dark:text-white"
              />
            </div>
            <div>
              <label className="text-xs">{t('tournaments.endTime', 'End Time')}</label>
              <input
                type="datetime-local"
                value={endTime}
                onChange={e => setEndTime(e.target.value)}
                className="w-full rounded-2xl border border-black/10 bg-[#FFFDF8] px-3 py-2 text-sm text-[#22313A] dark:border-[rgba(132,151,190,.14)] dark:bg-[#0C1423] dark:text-white"
              />
            </div>
          </div>

          <textarea
            value={rules}
            onChange={e => setRules(e.target.value)}
            placeholder={t('tournaments.rulesPlaceholder', "Rules (e.g., 'Heaviest single Bass')")}
            className="w-full rounded-2xl border border-black/10 bg-[#FFFDF8] px-3 py-2 text-sm text-[#22313A] dark:border-[rgba(132,151,190,.14)] dark:bg-[#0C1423] dark:text-white"
            rows={3}
          />

          <input
            type="number"
            value={prizePool}
            onChange={e => setPrizePool(e.target.value)}
            placeholder={t('tournaments.prizePlaceholder', `Prize Pool (${currency})`)}
            className="w-full rounded-2xl border border-black/10 bg-[#FFFDF8] px-3 py-2 text-sm text-[#22313A] dark:border-[rgba(132,151,190,.14)] dark:bg-[#0C1423] dark:text-white"
          />
        </div>

        <div className="flex space-x-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="w-full p-2 rounded-xl border border-black/10 bg-[#FFFDF8] font-bold text-[#22313A] dark:border-[rgba(132,151,190,.14)] dark:bg-[#0C1423] dark:text-white"
          >
            {t('common.cancel', 'Cancel')}
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            className="w-full p-2 rounded-xl bg-[#24313A] text-white dark:bg-[#FFFDF8] dark:text-[#21101A] font-black shadow-[0_10px_24px_rgba(241,100,46,0.28)]"
          >
            {t('common.save', 'Save')}
          </button>
        </div>
      </div>
    </div>
  );
};


const ReportsManagement: React.FC<AdminViewProps> = () => {
  const [reports, setReports] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [warningReport, setWarningReport] = useState<any | null>(null);
  const [warningMessage, setWarningMessage] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);
  const { confirm, confirmModal } = useAdminConfirmation();

  useEffect(() => {
    const qRef = query(collection(db, 'reports'), orderBy('createdAt', 'desc'), limit(200));
    const unsub = onSnapshot(
      qRef,
      (snap) => {
        setReports(
          snap.docs.map((d) => ({
            id: d.id,
            ...(d.data() as any),
          })),
        );
        setLoading(false);
      },
      (err) => {
        console.error('Failed to load reports', err);
        setLoading(false);
      },
    );
    return () => unsub();
  }, []);

  const withBusy = async (reportId: string, action: () => Promise<void>) => {
    setActionError(null);
    setBusyId(reportId);
    try {
      await action();
    } catch (err: any) {
      console.error('[admin reports] action failed', err);
      setActionError(err?.message || 'Admin action failed. If this just started, redeploy the admin report callable exports.');
    } finally {
      setBusyId((current) => (current === reportId ? null : current));
    }
  };

  const updateStatus = async (reportId: string, status: 'open' | 'reviewing' | 'resolved' | 'dismissed') => {
    await withBusy(reportId, async () => {
      await cfAdminUpdateUserReportStatus({ reportId, status } as any);
    });
  };

  const deleteReport = async (reportId: string) => {
    const ok = await confirm({
      title: 'Delete report?',
      message: 'This removes the report record only. The reported content is not deleted.',
      confirmText: 'Delete',
    });
    if (!ok) return;

    await withBusy(reportId, async () => {
      await cfAdminDeleteUserReport({ reportId } as any);
    });
  };

  const deleteReportedPost = async (report: any) => {
    if (!report?.postId) {
      setActionError('This report is not attached to a post.');
      return;
    }

    const ok = await confirm({
      title: 'Delete reported post?',
      message: 'This will delete the reported community post and mark this report resolved. This cannot be undone.',
      confirmText: 'Delete post',
    });
    if (!ok) return;

    await withBusy(report.id, async () => {
      await cfAdminDeleteReportedPost({ reportId: report.id, resolveReport: true } as any);
    });
  };

  const getReportedListingId = (report: any): string =>
    String(report?.listingId || report?.itemId || report?.marketplaceItemId || '').trim();

  const isBazaarListingReport = (report: any): boolean => {
    const type = String(report?.targetType || report?.reportType || '').trim().toLowerCase();
    return (
      ['bazaar_listing', 'marketplace_listing', 'marketplace_item'].includes(type) ||
      Boolean(getReportedListingId(report))
    );
  };

  const deleteReportedMarketplaceItem = async (report: any) => {
    const itemId = getReportedListingId(report);
    if (!itemId) {
      setActionError('This report is not attached to a Bazaar listing.');
      return;
    }

    const ok = await confirm({
      title: 'Delete reported listing?',
      message: 'This will permanently remove the Bazaar listing and its listing-scoped images, then mark this report resolved. This cannot be undone.',
      confirmText: 'Delete listing',
    });
    if (!ok) return;

    await withBusy(report.id, async () => {
      await cfAdminDeleteReportedMarketplaceItem({ reportId: report.id, resolveReport: true } as any);
    });
  };

  const openWarnModal = (report: any) => {
    setActionError(null);
    setWarningReport(report);
    setWarningMessage(
      'A community report connected to your content was reviewed by Roddo moderation. Please keep Baleen Bay safe, helpful, and respectful. Repeated violations may lead to content removal or account restrictions.',
    );
  };

  const submitWarning = async () => {
    if (!warningReport?.id) return;
    const cleanMessage = warningMessage.trim();
    if (cleanMessage.length < 8) {
      setActionError('Warning message is too short.');
      return;
    }

    await withBusy(warningReport.id, async () => {
      await cfAdminWarnUserForReport({ reportId: warningReport.id, message: cleanMessage } as any);
      setWarningReport(null);
      setWarningMessage('');
    });
  };

  return (
    <>
      <div className="mb-3 rounded-2xl border border-amber-300 bg-amber-50 px-3 py-3 text-xs font-semibold text-amber-900 dark:border-amber-400/25 dark:bg-amber-500/10 dark:text-amber-100">
        <div className="font-black">Bazaar rollout</div>
        <div className="mt-1">Classified listings are live. Roddo Checkout, seller payouts, and shipping automation remain disabled until the Stripe release gate is intentionally opened.</div>
      </div>
      <div className="space-y-2 rounded-2xl border border-[#D8C4AA] bg-[#FFFDF8] p-3 text-[#22313A] shadow-[0_18px_50px_rgba(15,23,42,0.08)] dark:border-[rgba(132,151,190,.14)] dark:bg-[#101827] dark:text-[#F4F6FA] dark:shadow-[0_18px_60px_rgba(0,0,0,0.35)]">
        {actionError ? (
          <div className="rounded-2xl border border-red-200 bg-red-50 px-3 py-2 text-sm font-bold text-red-700 dark:border-red-400/30 dark:bg-[#3A1720] dark:text-red-200">
            {actionError}
          </div>
        ) : null}

        {loading && <p className="py-4 text-sm font-semibold text-[#5B6577] dark:text-[#F4F6FA]">Loading reports…</p>}
        {!loading && reports.length === 0 && <p className="py-4 text-sm font-semibold text-[#5B6577] dark:text-[#F4F6FA]">No reports yet.</p>}

        {reports.map((report: any) => {
          const busy = busyId === report.id;
          const targetLabel = report.targetUserName || report.targetUserId || '—';
          const bazaarReport = isBazaarListingReport(report);
          const reportedListingId = getReportedListingId(report);
          const reportKindLabel = bazaarReport ? 'Bazaar listing' : report.postId ? 'Community post' : 'User report';

          return (
            <div key={report.id} className="rounded-xl border border-[#D8C4AA] bg-[#FFF8ED] p-3 dark:border-[rgba(132,151,190,.14)] dark:bg-[#0F1828]">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="text-sm font-black capitalize">
                      {String(report.reason || 'report').replaceAll('_', ' ')}
                    </div>
                    <span className="rounded-full border border-[#D8C4AA] bg-white/60 px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.12em] text-[#7B5E4C] dark:border-[rgba(132,151,190,.14)] dark:bg-white/5 dark:text-[#AEB8C9]">
                      {reportKindLabel}
                    </span>
                  </div>
                  <div className="mt-1 text-xs text-[#7B5E4C] dark:text-[#AEB8C9]">
                    Reporter: {report.reporterName || report.reporterUid || '—'} • Target: {targetLabel}
                  </div>
                  {report.postId ? (
                    <div className="mt-1 break-all text-[11px] text-[#7B5E4C] dark:text-[#AEB8C9]">Post: {report.postId}</div>
                  ) : null}
                  {reportedListingId ? (
                    <div className="mt-1 break-all text-[11px] text-[#7B5E4C] dark:text-[#AEB8C9]">Listing: {reportedListingId}</div>
                  ) : null}
                  {report.details ? <div className="mt-2 whitespace-pre-line text-sm">{report.details}</div> : null}
                  {report.warningMessage ? (
                    <div className="mt-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-900 dark:border-amber-300/30 dark:bg-[#332B1F] dark:text-amber-100">
                      Warning sent: {report.warningMessage}
                    </div>
                  ) : null}
                  {report.actionTaken ? (
                    <div className="mt-2 text-[11px] font-black uppercase tracking-[0.12em] text-[#5B6577] dark:text-[#F4F6FA]">
                      Action: {String(report.actionTaken).replaceAll('_', ' ')}
                    </div>
                  ) : null}
                </div>

                <div className="rounded-full px-3 py-1 text-[11px] font-extrabold border border-[#D8C4AA] dark:border-[rgba(132,151,190,.14)]">
                  {report.status || 'open'}
                </div>
              </div>

              <div className="mt-3 flex flex-wrap gap-2">
                <button type="button" disabled={busy} onClick={() => updateStatus(report.id, 'reviewing')} className="rounded-xl border border-black/10 px-3 py-2 text-xs font-bold disabled:opacity-50 dark:border-[rgba(132,151,190,.14)]">Mark reviewing</button>
                <button type="button" disabled={busy || !report.targetUserId} onClick={() => openWarnModal(report)} className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-black text-amber-800 disabled:opacity-50 dark:border-amber-300/30 dark:bg-[#332B1F] dark:text-amber-100">Warn user</button>
                {bazaarReport ? (
                  <button type="button" disabled={busy || !reportedListingId} onClick={() => deleteReportedMarketplaceItem(report)} className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs font-black text-red-700 disabled:opacity-50 dark:border-red-400/30 dark:bg-[#3A1720] dark:text-red-200">Delete reported listing</button>
                ) : report.postId ? (
                  <button type="button" disabled={busy} onClick={() => deleteReportedPost(report)} className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs font-black text-red-700 disabled:opacity-50 dark:border-red-400/30 dark:bg-[#3A1720] dark:text-red-200">Delete reported post</button>
                ) : null}
                <button type="button" disabled={busy} onClick={() => updateStatus(report.id, 'resolved')} className="rounded-xl border border-black/10 px-3 py-2 text-xs font-bold disabled:opacity-50 dark:border-[rgba(132,151,190,.14)]">Resolve</button>
                <button type="button" disabled={busy} onClick={() => updateStatus(report.id, 'dismissed')} className="rounded-xl border border-black/10 px-3 py-2 text-xs font-bold disabled:opacity-50 dark:border-[rgba(132,151,190,.14)]">Dismiss</button>
                <button type="button" disabled={busy} onClick={() => deleteReport(report.id)} className="rounded-xl border border-red-200 px-3 py-2 text-xs font-bold text-red-600 disabled:opacity-50 dark:border-red-400/30 dark:text-red-300">Delete report</button>
              </div>
            </div>
          );
        })}
      </div>

      {warningReport ? (
        <div className="roddo-admin-mobile-modal fixed inset-0 z-[9999] flex items-center justify-center bg-black/45 px-3 pb-[max(12px,env(safe-area-inset-bottom))] pt-[max(12px,env(safe-area-inset-top))] backdrop-blur-sm sm:p-4" onClick={() => setWarningReport(null)}>
          <div
            className="max-h-[calc(var(--roddo-visual-vh,100dvh)-max(24px,env(safe-area-inset-top))-max(24px,env(safe-area-inset-bottom)))] w-full max-w-lg overflow-y-auto rounded-[28px] border-2 border-black/10 bg-[#FFFDF8] p-5 text-[#22313A] shadow-[0_24px_80px_rgba(0,0,0,0.22)] dark:border-[rgba(132,151,190,.14)] dark:bg-[#111827] dark:text-white"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-xs font-black uppercase tracking-[0.18em] text-[#f1642e]">Moderation warning</div>
                <h3 className="mt-1 text-xl font-black">Warn {warningReport.targetUserName || warningReport.targetUserId || 'user'}</h3>
                <p className="mt-1 text-sm font-semibold text-[#5B6577] dark:text-[#F4F6FA]">
                  This sends a notification to the reported user and records the action on the report.
                </p>
              </div>
              <button type="button" onClick={() => setWarningReport(null)} className="rounded-full border border-black/10 px-3 py-1 text-sm font-black dark:border-[rgba(132,151,190,.14)]">×</button>
            </div>

            <textarea
              value={warningMessage}
              onChange={(e) => setWarningMessage(e.target.value)}
              rows={6}
              className="mt-4 w-full rounded-2xl border border-black/10 bg-[#f8f4ee] p-3 text-sm font-semibold outline-none focus:ring-2 focus:ring-[#f1642e]/30 dark:border-[rgba(132,151,190,.14)] dark:bg-[#0C1423]"
              maxLength={1000}
            />
            <div className="mt-1 text-right text-[11px] font-bold text-[#7B5E4C] dark:text-[#AEB8C9]">
              {warningMessage.trim().length}/1000
            </div>

            <div className="mt-4 flex gap-2">
              <button
                type="button"
                onClick={() => setWarningReport(null)}
                className="flex-1 rounded-2xl border border-black/10 bg-[#FFFDF8] px-4 py-3 text-sm font-black dark:border-[rgba(132,151,190,.14)] dark:bg-[#0C1423]"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={busyId === warningReport.id || warningMessage.trim().length < 8}
                onClick={submitWarning}
                className="flex-1 rounded-2xl bg-[#f1642e] px-4 py-3 text-sm font-black text-white shadow-[0_10px_24px_rgba(241,100,46,0.28)] disabled:opacity-50"
              >
                Send warning
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {confirmModal}
    </>
  );
};






const SupportTicketsManagement: React.FC = () => {
  const [tickets, setTickets] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const { confirm, confirmModal } = useAdminConfirmation();

  useEffect(() => {
    const qRef = query(collection(db, 'supportTickets'), orderBy('createdAt', 'desc'), limit(200));

    const unsub = onSnapshot(
      qRef,
      (snap) => {
        setTickets(
          snap.docs.map((d) => ({
            id: d.id,
            ...(d.data() as any),
          })),
        );
        setLoading(false);
      },
      (err) => {
        console.error('Failed to load support tickets', err);
        setLoading(false);
      },
    );

    return () => unsub();
  }, []);

  const updateTicketStatus = async (
    ticketId: string,
    status: 'open' | 'reviewing' | 'resolved' | 'closed',
  ) => {
    try {
      setBusyId(ticketId);
      await cfAdminUpdateSupportTicket({ ticketId, status } as any);
    } catch (err) {
      console.error('Failed to update support ticket', err);
    } finally {
      setBusyId((current) => (current === ticketId ? null : current));
    }
  };

  const clearTicket = async (ticketId: string) => {
    const ok = await confirm({
      title: 'Clear support ticket?',
      message: 'This removes the ticket from the admin queue. Use this only after it has been handled or is no longer needed.',
      confirmText: 'Clear',
    });
    if (!ok) return;

    try {
      setBusyId(ticketId);
      await deleteDoc(doc(db, 'supportTickets', ticketId));
    } catch (err) {
      console.error('Failed to clear support ticket', err);
    } finally {
      setBusyId((current) => (current === ticketId ? null : current));
    }
  };

  return (
    <>
      <div className="mb-3 rounded-2xl border border-amber-300 bg-amber-50 px-3 py-3 text-xs font-semibold text-amber-900 dark:border-amber-400/25 dark:bg-amber-500/10 dark:text-amber-100">
        <div className="font-black">Bazaar rollout</div>
        <div className="mt-1">Classified listings are live. Roddo Checkout, seller payouts, and shipping automation remain disabled until the Stripe release gate is intentionally opened.</div>
      </div>
      <div className="space-y-2 rounded-2xl border border-[#D8C4AA] bg-[#FFFDF8] p-3 text-[#22313A] shadow-[0_18px_50px_rgba(15,23,42,0.08)] dark:border-[rgba(132,151,190,.14)] dark:bg-[#101827] dark:text-[#F4F6FA] dark:shadow-[0_18px_60px_rgba(0,0,0,0.35)]">
      {loading && (
        <p className="py-4 text-sm font-semibold text-[#5B6577] dark:text-[#F4F6FA]">
          Loading support tickets…
        </p>
      )}

      {!loading && tickets.length === 0 && (
        <p className="py-4 text-sm font-semibold text-[#5B6577] dark:text-[#F4F6FA]">
          No support tickets yet.
        </p>
      )}

      {tickets.map((ticket: any) => {
        const isBusy = busyId === ticket.id;
        return (
          <div
            key={ticket.id}
            className="rounded-xl border border-[#D8C4AA] bg-[#FFF8ED] p-3 dark:border-[rgba(132,151,190,.14)] dark:bg-[#0F1828]"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm font-black">
                  {ticket.subject || 'Untitled support ticket'}
                </div>

                <div className="mt-1 text-xs text-[#7B5E4C] dark:text-[#AEB8C9]">
                  From: {ticket.userName || ticket.contactEmail || ticket.userEmail || ticket.userId || 'Unknown user'}
                </div>

                <div className="mt-1 text-[11px] text-[#7B5E4C] dark:text-[#AEB8C9]">
                  Category: {ticket.category || 'general'} • Priority: {ticket.priority || 'normal'}
                </div>

                {ticket.contactEmail ? (
                  <div className="mt-1 text-[11px] text-[#7B5E4C] dark:text-[#AEB8C9]">
                    Reply: {ticket.contactEmail}
                  </div>
                ) : null}

                {ticket.message ? (
                  <div className="mt-2 whitespace-pre-line text-sm">
                    {ticket.message}
                  </div>
                ) : null}

                {(ticket.deviceInfo || ticket.appVersion) && (
                  <div className="mt-2 text-[11px] text-[#7B5E4C] dark:text-[#AEB8C9]">
                    {ticket.appVersion ? `App: ${ticket.appVersion}` : ''}
                    {ticket.appVersion && ticket.deviceInfo ? ' • ' : ''}
                    {ticket.deviceInfo ? `Device: ${ticket.deviceInfo}` : ''}
                  </div>
                )}
              </div>

              <div className="rounded-full px-3 py-1 text-[11px] font-extrabold border border-[#D8C4AA] dark:border-[rgba(132,151,190,.14)]">
                {ticket.status || 'open'}
              </div>
            </div>

            <div className="mt-3 flex flex-wrap gap-2">
              <button
                type="button"
                disabled={isBusy}
                onClick={() => updateTicketStatus(ticket.id, 'reviewing')}
                className="rounded-xl border border-[#D8C4AA] bg-[#FFFDF8] px-3 py-2 text-xs font-bold text-[#22313A] shadow-sm transition hover:bg-[#FFF8ED] disabled:opacity-60 disabled:text-[#8A735F] dark:border-[rgba(180,195,220,.16)] dark:bg-[#0C1423] dark:text-[#F4F6FA] dark:hover:bg-[#0F1828] dark:disabled:text-[#7F8BA0]"
              >
                Mark reviewing
              </button>
              <button
                type="button"
                disabled={isBusy}
                onClick={() => updateTicketStatus(ticket.id, 'resolved')}
                className="rounded-xl border border-[#D8C4AA] bg-[#FFFDF8] px-3 py-2 text-xs font-bold text-[#22313A] shadow-sm transition hover:bg-[#FFF8ED] disabled:opacity-60 disabled:text-[#8A735F] dark:border-[rgba(180,195,220,.16)] dark:bg-[#0C1423] dark:text-[#F4F6FA] dark:hover:bg-[#0F1828] dark:disabled:text-[#7F8BA0]"
              >
                Resolve
              </button>
              <button
                type="button"
                disabled={isBusy}
                onClick={() => updateTicketStatus(ticket.id, 'closed')}
                className="rounded-xl border border-[#D8C4AA] bg-[#FFFDF8] px-3 py-2 text-xs font-bold text-[#22313A] shadow-sm transition hover:bg-[#FFF8ED] disabled:opacity-60 disabled:text-[#8A735F] dark:border-[rgba(180,195,220,.16)] dark:bg-[#0C1423] dark:text-[#F4F6FA] dark:hover:bg-[#0F1828] dark:disabled:text-[#7F8BA0]"
              >
                Close
              </button>
              <button
                type="button"
                disabled={isBusy}
                onClick={() => clearTicket(ticket.id)}
                className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs font-bold text-red-700 transition hover:bg-red-100 disabled:opacity-60 disabled:text-[#8A735F] dark:border-red-400/40 dark:bg-[#3A1720] dark:text-red-100 dark:hover:bg-[#4A1D28] dark:disabled:text-red-200"
              >
                Clear
              </button>
            </div>
          </div>
        );
      })}
      </div>
      {confirmModal}
    </>
  );
};


const AccountDeletionRequestsManagement: React.FC<Pick<AdminViewProps, 'users' | 'onUserAndPostsDeleted'>> = ({
  users,
  onUserAndPostsDeleted,
}) => {
  const [requests, setRequests] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const { confirm, confirmModal } = useAdminConfirmation();

  useEffect(() => {
    const qRef = query(collection(db, 'accountDeletionRequests'), limit(200));

    const unsub = onSnapshot(
      qRef,
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }));
        rows.sort((a, b) => {
          const getTime = (value: any) =>
            typeof value === 'number'
              ? value
              : typeof value?.toMillis === 'function'
                ? value.toMillis()
                : 0;
          const aTime = getTime(a.updatedAt) || getTime(a.requestedAt) || getTime(a.clientSubmittedAt);
          const bTime = getTime(b.updatedAt) || getTime(b.requestedAt) || getTime(b.clientSubmittedAt);
          return bTime - aTime;
        });
        setRequests(rows);
        setLoading(false);
      },
      (err) => {
        console.error('Failed to load account deletion requests', err);
        setMessage('Unable to load account deletion requests. Check Firestore rules/admin claim.');
        setLoading(false);
      },
    );

    return () => unsub();
  }, []);

  const formatDate = (value: any) => {
    const ms =
      typeof value === 'number'
        ? value
        : typeof value?.toMillis === 'function'
          ? value.toMillis()
          : null;
    if (!ms) return 'Unknown time';
    try {
      return new Date(ms).toLocaleString();
    } catch {
      return 'Unknown time';
    }
  };

  const statusTone = (status: string) => {
    const normalized = String(status || 'requested').toLowerCase();
    if (['completed', 'deleted', 'processed'].includes(normalized)) return 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-400/30 dark:bg-[#123326] dark:text-emerald-200';
    if (['failed', 'error'].includes(normalized)) return 'border-red-200 bg-red-50 text-red-700 dark:border-red-400/30 dark:bg-[#3A1720] dark:text-red-200';
    if (['cancelled', 'canceled'].includes(normalized)) return 'border-slate-200 bg-slate-50 text-slate-600 dark:border-slate-400/25 dark:bg-[#242437] dark:text-slate-200';
    if (['processing', 'reviewing', 'scheduled'].includes(normalized)) return 'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-400/30 dark:bg-[#3A2A16] dark:text-amber-200';
    return 'border-[#D8C4AA] bg-[#FFFDF8] text-[#5B6577] dark:border-[rgba(132,151,190,.14)] dark:bg-[#0C1423] dark:text-[#F4F6FA]';
  };

  const runProcessor = async () => {
    const ok = await confirm({
      title: 'Run deletion processor?',
      message: 'This may permanently delete or anonymize due accounts depending on backend grace-period rules.',
      confirmText: 'Run processor',
    });
    if (!ok) return;

    try {
      setBusy(true);
      setMessage(null);
      const result = await cfAdminProcessAccountDeletionRequests({ limit: 25 } as any);
      const data: any = result?.data || {};
      setMessage(
        `Processor finished. Processed: ${data.processed ?? data.completed ?? 0}${data.failed != null ? ` • Failed: ${data.failed}` : ''}`,
      );
    } catch (err: any) {
      console.error('Failed to run account deletion processor', err);
      setMessage(err?.message || 'Account deletion processor failed.');
    } finally {
      setBusy(false);
    }
  };

  const clearRequest = async (requestId: string) => {
    const ok = await confirm({
      title: 'Clear deletion request?',
      message: 'This removes the request from the admin queue only. Use this for completed, cancelled, or test requests.',
      confirmText: 'Clear',
    });
    if (!ok) return;

    try {
      setBusyId(requestId);
      await deleteDoc(doc(db, 'accountDeletionRequests', requestId));
    } catch (err: any) {
      console.error('Failed to clear account deletion request', err);
      setMessage(err?.message || 'Failed to clear deletion request.');
    } finally {
      setBusyId((current) => (current === requestId ? null : current));
    }
  };

  const deleteRequestedAccount = async (request: any) => {
    const uid = String(request.userId || request.uid || request.id || '').trim();
    if (!uid) {
      setMessage('Missing uid on deletion request. Cannot delete from Admin.');
      return;
    }

    const label = request.email || request.authEmail || request.displayName || uid;
    const ok = await confirm({
      title: `Delete account ${label}?`,
      message: 'This will run the same admin delete-user flow from the Users tab, then clear this deletion request from the queue. This cannot be undone.',
      confirmText: 'Delete account',
    });
    if (!ok) return;

    try {
      setBusyId(request.id);
      setMessage(null);
      await Promise.resolve(onUserAndPostsDeleted(uid));
      await deleteDoc(doc(db, 'accountDeletionRequests', request.id));
      setMessage(`Deleted account and cleared request: ${label}`);
    } catch (err: any) {
      console.error('Failed to delete requested account', err);
      setMessage(err?.message || 'Failed to delete requested account.');
    } finally {
      setBusyId((current) => (current === request.id ? null : current));
    }
  };

  return (
    <>
      <div className="space-y-3 rounded-2xl border border-[#D8C4AA] bg-[#FFFDF8] p-3 text-[#22313A] shadow-[0_18px_50px_rgba(15,23,42,0.08)] dark:border-[rgba(132,151,190,.14)] dark:bg-[#101827] dark:text-[#F4F6FA] dark:shadow-[0_18px_60px_rgba(0,0,0,0.35)]">
      <div className="flex flex-col gap-3 rounded-2xl border border-[#D8C4AA] bg-[#FFF8ED] p-4 dark:border-[rgba(132,151,190,.14)] dark:bg-[#0F1828] sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="text-[11px] font-black uppercase tracking-[0.18em] text-[#7B5E4C] dark:text-[#AEB8C9]">Privacy queue</div>
          <h3 className="mt-1 text-lg font-black">Account deletion requests</h3>
          <p className="mt-1 max-w-2xl text-xs font-semibold leading-5 text-[#5B6577] dark:text-[#F4F6FA]">
            These are user-initiated account deletion requests. The backend should own the actual delete/anonymize work so client apps never hard-delete sensitive data directly.
          </p>
        </div>
        <button
          type="button"
          disabled={busy}
          onClick={runProcessor}
          className="rounded-xl border border-red-200 bg-red-50 px-4 py-2.5 text-xs font-black text-red-700 shadow-sm transition hover:bg-red-100 disabled:opacity-60 dark:border-red-400/40 dark:bg-[#3A1720] dark:text-red-100 dark:hover:bg-[#4A1D28]"
        >
          {busy ? 'Processing…' : 'Run processor'}
        </button>
      </div>

      {message ? (
        <div className="rounded-xl border border-[#D8C4AA] bg-[#FFFDF8] px-3 py-2 text-xs font-bold text-[#5B6577] dark:border-[rgba(132,151,190,.14)] dark:bg-[#0C1423] dark:text-[#F4F6FA]">
          {message}
        </div>
      ) : null}

      {loading && (
        <p className="py-4 text-sm font-semibold text-[#5B6577] dark:text-[#F4F6FA]">
          Loading deletion requests…
        </p>
      )}

      {!loading && requests.length === 0 && (
        <div className="rounded-xl border border-dashed border-[#D8C4AA] bg-[#FFF8ED] p-4 text-sm font-semibold text-[#5B6577] dark:border-[rgba(132,151,190,.14)] dark:bg-[#0F1828] dark:text-[#F4F6FA]">
          No account deletion requests yet.
        </div>
      )}

      <div className="space-y-2 max-h-[62vh] overflow-y-auto">
        {requests.map((request: any) => {
          const isBusy = busyId === request.id;
          const status = request.status || request.accountDeletionStatus || 'requested';
          const requestedAt = request.requestedAt || request.createdAt || request.clientSubmittedAt;
          const scheduledAt = request.scheduledDeletionAt || request.accountDeletionScheduledAt || request.scheduledFor;
          const requestUid = String(request.userId || request.uid || request.id || '').trim();
          const linkedUser = requestUid ? (users as any)?.[requestUid] : null;
          const displayLabel =
            request.displayName ||
            linkedUser?.displayName ||
            linkedUser?.name ||
            linkedUser?.username ||
            request.userName ||
            request.email ||
            request.authEmail ||
            requestUid ||
            request.id;
          const emailLabel = request.email || request.authEmail || linkedUser?.email || linkedUser?.authEmail || '';

          return (
            <div key={request.id} className="rounded-xl border border-[#D8C4AA] bg-[#FFF8ED] p-3 dark:border-[rgba(132,151,190,.14)] dark:bg-[#0F1828]">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0">
                  <div className="text-sm font-black">
                    {displayLabel}
                  </div>
                  <div className="mt-1 text-xs text-[#7B5E4C] dark:text-[#AEB8C9]">
                    UID: {requestUid || request.id}
                  </div>
                  {emailLabel ? (
                    <div className="mt-1 text-xs text-[#7B5E4C] dark:text-[#AEB8C9]">
                      Email: {emailLabel}
                    </div>
                  ) : null}
                  <div className="mt-1 text-[11px] text-[#7B5E4C] dark:text-[#AEB8C9]">
                    Requested: {formatDate(requestedAt)}
                    {scheduledAt ? ` • Scheduled: ${formatDate(scheduledAt)}` : ''}
                  </div>
                  {request.reason ? (
                    <div className="mt-2 whitespace-pre-line rounded-xl border border-[#D8C4AA] bg-[#FFFDF8] p-3 text-sm dark:border-[rgba(132,151,190,.14)] dark:bg-[#0C1423]">
                      {request.reason}
                    </div>
                  ) : null}
                </div>

                <span className={["w-fit rounded-full border px-3 py-1 text-[11px] font-black uppercase tracking-[0.08em]", statusTone(status)].join(' ')}>
                  {status}
                </span>
              </div>

              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  type="button"
                  disabled={isBusy || !(request.userId || request.uid || request.id)}
                  onClick={() => deleteRequestedAccount(request)}
                  className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs font-black text-red-700 shadow-sm transition hover:bg-red-100 disabled:opacity-60 dark:border-red-400/40 dark:bg-[#3A1720] dark:text-red-100 dark:hover:bg-[#4A1D28]"
                >
                  Delete account now
                </button>
                <button
                  type="button"
                  disabled={isBusy}
                  onClick={() => clearRequest(request.id)}
                  className="rounded-xl border border-[#D8C4AA] bg-[#FFFDF8] px-3 py-2 text-xs font-bold text-[#22313A] shadow-sm transition hover:bg-[#FFF8ED] disabled:opacity-60 dark:border-[rgba(180,195,220,.16)] dark:bg-[#0C1423] dark:text-[#F4F6FA] dark:hover:bg-[#0F1828]"
                >
                  Clear queue item
                </button>
              </div>
            </div>
          );
        })}
      </div>
      </div>
      {confirmModal}
    </>
  );
};



// ----------------------------
// DEV TOOLS (ADMIN-GATED CLEANUP UTILITIES)
// ----------------------------
function DevToolsSection({
  users,
  currentUserId,
}: {
  users: Record<string, AppUser>;
  currentUserId?: string;
}) {
  const [selectedUid, setSelectedUid] = useState(currentUserId || '');
  const [manualUid, setManualUid] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [busy, setBusy] = useState<'resetDry' | 'reset' | 'recomputeDry' | 'recompute' | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [result, setResult] = useState<any>(null);
  const { confirm, confirmModal } = useAdminConfirmation();

  const userOptions = useMemo(() => {
    return Object.values(users || {})
      .filter((u: any) => String(u?.uid || u?.id || '').trim())
      .map((u: any) => {
        const uid = String(u.uid || u.id);
        const label = String(u.name || u.displayName || u.username || u.email || uid);
        return { uid, label };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [users]);

  const targetUid = (manualUid.trim() || selectedUid || '').trim();

  const callDevFunction = useCallback(async (name: 'devResetUserProgress' | 'devRecomputeUserProgress', payload: any) => {
    const functions = getFunctions(getApp(), 'us-central1');
    const fn = httpsCallable(functions, name);
    const res = await fn(payload);
    return (res as any).data;
  }, []);

  const run = useCallback(
    async (mode: 'resetDry' | 'reset' | 'recomputeDry' | 'recompute') => {
      if (!targetUid) {
        setMessage('Choose or paste a user UID first.');
        return;
      }

      const isReset = mode === 'reset' || mode === 'resetDry';
      const dryRun = mode === 'resetDry' || mode === 'recomputeDry';

      if (!dryRun) {
        const required = isReset ? 'RESET' : 'RECOMPUTE';
        if (confirmText.trim().toUpperCase() !== required) {
          setMessage(`Type ${required} to confirm this action.`);
          return;
        }

        const ok = await confirm({
          title: isReset ? 'Reset user progress?' : 'Recompute user progress?',
          message: isReset
            ? `This deletes catch, progression, and FishIQ mirror data for ${targetUid}, then zeroes progression fields. This cannot be undone.`
            : `This clears progression mirrors for ${targetUid} and replays FishIQ private events for that user.`,
          confirmText: isReset ? 'Reset' : 'Recompute',
        });
        if (!ok) return;
      }

      setBusy(mode);
      setMessage(null);
      setResult(null);

      try {
        const data = await callDevFunction(isReset ? 'devResetUserProgress' : 'devRecomputeUserProgress', {
          userId: targetUid,
          dryRun,
        });
        setResult(data);
        setMessage(
          dryRun
            ? 'Dry run complete. Nothing was changed.'
            : isReset
            ? 'Reset complete for selected user.'
            : 'Recompute complete for selected user.',
        );
        if (!dryRun) setConfirmText('');
      } catch (err: any) {
        console.error('[Admin Dev Tools] failed', err);
        setMessage(err?.message || 'Dev tool failed. Check function logs/admin permissions.');
      } finally {
        setBusy(null);
      }
    },
    [callDevFunction, confirm, confirmText, targetUid],
  );

  return (
    <>
      <div className="space-y-4">
      <AdminPanel className="p-4 sm:p-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <div className="text-[11px] font-black uppercase tracking-[0.2em] text-[#7B5E4C] dark:text-[#AEB8C9]">
              Beta cleanup tools
            </div>
            <h3 className="mt-1 text-xl font-black text-[#22313A] dark:text-white">Dev utilities</h3>
            <p className="mt-2 text-sm font-semibold leading-6 text-[#5B6577] dark:text-[#F4F6FA]">
              Admin-only tools for clearing old test catch/progression data before beta users enter. Use dry run first.
            </p>
          </div>
          <span className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-black uppercase tracking-[0.12em] text-amber-800 dark:border-amber-400/30 dark:bg-[#3A2A16] dark:text-amber-200">
            destructive
          </span>
        </div>
      </AdminPanel>

      <AdminPanel className="p-4 sm:p-5">
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block">
            <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#7B5E4C] dark:text-[#AEB8C9]">
              Select user
            </span>
            <select
              value={selectedUid}
              onChange={(e) => setSelectedUid(e.target.value)}
              className="mt-2 w-full rounded-xl border border-[#D8C4AA] bg-[#FFFDF8] px-3 py-3 text-sm font-bold text-[#22313A] outline-none dark:border-[rgba(132,151,190,.14)] dark:bg-[#0C1423] dark:text-white"
            >
              <option value="">Choose user…</option>
              {userOptions.map((u) => (
                <option key={u.uid} value={u.uid}>
                  {u.label} — {u.uid.slice(0, 8)}…
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#7B5E4C] dark:text-[#AEB8C9]">
              Or paste UID
            </span>
            <input
              value={manualUid}
              onChange={(e) => setManualUid(e.target.value)}
              placeholder="Optional manual UID override"
              className="mt-2 w-full rounded-xl border border-[#D8C4AA] bg-[#FFFDF8] px-3 py-3 text-sm font-bold text-[#22313A] outline-none dark:border-[rgba(132,151,190,.14)] dark:bg-[#0C1423] dark:text-white"
            />
          </label>
        </div>

        <div className="mt-4 rounded-2xl border border-[#E5D4BE] bg-[#FFF8ED] p-3 text-xs font-bold leading-5 text-[#5B6577] dark:border-[rgba(132,151,190,.14)] dark:bg-[#0C1423] dark:text-[#AEB8C9]">
          <p><strong>Target:</strong> {targetUid || 'none selected'}</p>
          <p className="mt-1">
            Reset deletes user-scoped catch/progression/FishIQ mirror data. Recompute rebuilds progression from that user's private FishIQ events.
          </p>
        </div>

        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <button
            type="button"
            disabled={!!busy || !targetUid}
            onClick={() => run('resetDry')}
            className="rounded-xl border border-[#D8C4AA] bg-white px-4 py-3 text-sm font-black text-[#314653] shadow-sm disabled:opacity-50 dark:border-[rgba(132,151,190,.14)] dark:bg-[#0C1423] dark:text-white"
          >
            {busy === 'resetDry' ? 'Checking…' : 'Dry Run Reset'}
          </button>
          <button
            type="button"
            disabled={!!busy || !targetUid}
            onClick={() => run('recomputeDry')}
            className="rounded-xl border border-[#D8C4AA] bg-white px-4 py-3 text-sm font-black text-[#314653] shadow-sm disabled:opacity-50 dark:border-[rgba(132,151,190,.14)] dark:bg-[#0C1423] dark:text-white"
          >
            {busy === 'recomputeDry' ? 'Checking…' : 'Dry Run Recompute'}
          </button>
        </div>

        <label className="mt-4 block">
          <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#7B5E4C] dark:text-[#AEB8C9]">
            Confirmation
          </span>
          <input
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="Type RESET or RECOMPUTE"
            className="mt-2 w-full rounded-xl border border-[#D8C4AA] bg-[#FFFDF8] px-3 py-3 text-sm font-bold text-[#22313A] outline-none dark:border-[rgba(132,151,190,.14)] dark:bg-[#0C1423] dark:text-white"
          />
        </label>

        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <button
            type="button"
            disabled={!!busy || !targetUid}
            onClick={() => run('reset')}
            className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-black text-red-700 shadow-sm disabled:opacity-50 dark:border-red-400/30 dark:bg-[#3A1418] dark:text-red-200"
          >
            {busy === 'reset' ? 'Resetting…' : 'Reset User Progress'}
          </button>
          <button
            type="button"
            disabled={!!busy || !targetUid}
            onClick={() => run('recompute')}
            className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-black text-amber-800 shadow-sm disabled:opacity-50 dark:border-amber-400/30 dark:bg-[#3A2A16] dark:text-amber-200"
          >
            {busy === 'recompute' ? 'Recomputing…' : 'Recompute Progress'}
          </button>
        </div>

        {message && (
          <div className="mt-4 rounded-xl border border-[#D8C4AA] bg-[#FFFDF8] px-3 py-2 text-sm font-bold text-[#314653] dark:border-[rgba(132,151,190,.14)] dark:bg-[#0C1423] dark:text-white">
            {message}
          </div>
        )}

        {result && (
          <pre className="mt-4 max-h-72 overflow-auto rounded-xl border border-[#D8C4AA] bg-[#101827] p-3 text-xs font-semibold text-slate-100 dark:border-[rgba(132,151,190,.14)]">
            {JSON.stringify(result, null, 2)}
          </pre>
        )}
      </AdminPanel>
      </div>
      {confirmModal}
    </>
  );
}


export default AdminView;
